import React, { useEffect, useState, useRef, useCallback } from 'react';
import html2canvas from 'html2canvas';
import CityPicker from './CityPicker';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import el from "date-fns/locale/nl";
import axios from 'axios';
import moment from 'moment';
import stationMappings from '../stations.json';
import nlStations from '../nl-stations.json';
import '../App.css';
import { FaExchangeAlt, FaTrain } from 'react-icons/fa';

registerLocale("nl", el);

// Helper function to parse a date string in dd-mm-yyyy format
function parseDate(dateStr) {
  const [day, month, year] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day); // month is zero-based
}

function formatDate(inputDate) {
  // Check if the input is null, undefined, or in an incorrect format using a simple regex
  const datePattern = /^\d{2}-\d{2}-\d{4}$/;
  
  if (!inputDate || inputDate === "autoDDMMYYYY" || !datePattern.test(inputDate)) {
    if (inputDate !== "autoDDMMYYYY") {
      console.log(inputDate);
      console.warn('Invalid or null date. Setting default date.');
    } else {
      // Force dd-MM-yyyy pattern
      return moment().add(2, 'months').format('DD-MM-YYYY');
    }
    
    // Set the default date to two months from today
    const defaultDate = moment().add(2, 'months').format('MM-DD-YYYY');
    return defaultDate;
  }
  
  // Split the date into day, month, and year
  const [day, month, year] = inputDate.split('-');

  // Validate day, month, and year values
  if (parseInt(day, 10) > 31 || parseInt(day, 10) < 1 ||
      parseInt(month, 10) > 12 || parseInt(month, 10) < 1 ||
      year.length !== 4)
  {
    // Set the default date to two months from today
    const defaultDate = moment().add(2, 'months').format('MM-DD-YYYY');
    return defaultDate;
  }

  // Reformat the date to MM-DD-YYYY
  const reformattedDate = `${month}-${day}-${year}`;

  return reformattedDate;
}

// Add these utility classes/constants at the top of the file, outside the component
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const MAX_CONCURRENT_REQUESTS = 3;
const MAX_RETRIES = 3;
const BASE_RETRY_DELAY = 1000;
const MAX_RETRY_DELAY = 10000;

// Add near the top of the file with other constants
const ENABLE_EXTRA_REQUESTS = false; // Configuration flag to enable/disable extra requests

class RequestQueue {
  constructor() {
    this.queue = [];
    this.activeRequests = 0;
  }

  async add(request) {
    if (this.activeRequests >= MAX_CONCURRENT_REQUESTS) {
      // Wait for a slot to open up
      await new Promise(resolve => this.queue.push(resolve));
    }

    this.activeRequests++;
    try {
      return await request();
    } finally {
      this.activeRequests--;
      if (this.queue.length > 0) {
        const next = this.queue.shift();
        next();
      }
    }
  }
}

class TripCache {
  constructor() {
    this.cache = new Map();
  }

  get(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }
    return null;
  }

  set(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  clear() {
    this.cache.clear();
  }
}

// Move this before the TripOptions component definition, near other constants
const stationNameMapping = {};

// Keep the simple unslugify implementation
function unslugify(slug) {
    if (stationNameMapping[slug]) {
        return stationNameMapping[slug];
    }

    // Fallback: replace hyphens with spaces and capitalize each word
    return slug
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
}

const TripOptions = () => {
  const { origin: paramOrigin, destination: paramDestination, departure: paramDeparture, return: paramReturn } = useParams();
  const navigate = useNavigate();
  const [origin, setOrigin] = useState(paramOrigin || 'Amsterdam');
  const [destination, setDestination] = useState(paramDestination || 'Berlin');
  const [tripOptions, setTripOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nsOrigin, setNsOrigin] = useState('');
  const [nsDestination, setNsDestination] = useState('');
  const [sortOption, setSortOption] = useState('best');
  const [loadingAvailability, setLoadingAvailability] = useState({});
  const [availableTrips, setAvailableTrips] = useState({});
  const [journeyWidthInPixels, setJourneyWidthInPixels] = useState(0);
  const [visibleVendorBoxes, setVisibleVendorBoxes] = useState({});
  const [showMoreOffers, setShowMoreOffers] = useState({});
  const [departureDate, setDepartureDate] = useState(paramDeparture || formatDate('autoDDMMYYYY'));
  const [returnDate, setReturnDate] = useState(paramReturn || formatDate('autoDDMMYYYY'));
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
  const [initialized, setInitialized] = useState(false);
  const journeyRef = useRef(null);
  const scrollRef = useRef(null);
  const vendorBoxRefs = useRef({});
  const departureDateRef = useRef(null);
  const returnDateRef = useRef(null);
  const uniqueTripsRef = useRef(new Set());
  const requestCountRef = useRef(0);
  const previousLastDepartureTimeRef = useRef(null);
  const [passengerCounts, setPassengerCounts] = useState({
    adults: 1,
    babies: 0,
    children4to5: 0,
    children6to11: 0,
    children12to14: 0,
    youth15: 0,
    youth16to25: 0,
    seniors: 0,
  });
  const [isViewingReturn, setIsViewingReturn] = useState(false);
  const [travelDate, setTravelDate] = useState(departureDate);
  const [departureDatePickerOpen, setDepartureDatePickerOpen] = useState(false);
  const [returnDatePickerOpen, setReturnDatePickerOpen] = useState(false);
  // Add these state variables at the top of your component
  const [mobileTooltipVisible, setMobileTooltipVisible] = useState(false);
  const [mobileTooltipText, setMobileTooltipText] = useState('');
  const [isLoadingMore, setIsLoadingMore] = useState(false); // Add this new state
  // At the top with other state declarations
  const [loadingState, setLoadingState] = useState('loading'); // 'loading', 'complete', 'hiding', 'hidden'

  // Add new state for saved trips feature
  const [savedOutboundTrip, setSavedOutboundTrip] = useState(null);
  const [savedReturnTrip, setSavedReturnTrip] = useState(null);
  const [isShowingSavedTripsOnly, setIsShowingSavedTripsOnly] = useState(false);

  // Add ref for saved trips container
  const savedTripsContainerRef = useRef(null);

  // Add new refs for request management
  const requestQueueRef = useRef(new RequestQueue());
  const tripCacheRef = useRef(new TripCache());
  const abortControllerRef = useRef(null);

  // Add connection status state
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Add connection status monitoring
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Provide default values for local development. In production, this is passed by WordPress. 
  const tripPlannerData = window.tripPlannerData ?? {
    origin: "Amsterdam",
    destination: "Berlin",
    departureDate: formatDate('autoDDMMYYYY'),
    returnDate: (() => {
      const date = moment(formatDate('autoDDMMYYYY'), 'MM-DD-YYYY');
      return date.add(5, 'days').format('DD-MM-YYYY');
    })(),
    baseUrl: ''
  };

  const noPriceAPIOperators = ['EUROSTAR', 'SNCF'];

  // Add new state for tracking request phase
  const [requestPhase, setRequestPhase] = useState('initial'); // 'initial', 'more', or 'extra'

  useEffect(() => {
    if (!initialized) {
      const originalDepartureDate = paramDeparture || formatDate('autoDDMMYYYY');
      const autoReturnDate = moment(originalDepartureDate, 'DD-MM-YYYY').add(5, 'days').format('DD-MM-YYYY');
      
      let originalReturnDate = paramReturn || autoReturnDate;
      
      // Validate that returnDate is after departureDate
      const departureDate = moment(originalDepartureDate, 'DD-MM-YYYY');
      const returnDate = moment(originalReturnDate, 'DD-MM-YYYY');
      
      if (!returnDate.isValid() || returnDate.isSameOrBefore(departureDate)) {
        console.warn(`Invalid return date provided: ${paramReturn}. Using auto-calculated return date: ${autoReturnDate}.`);
        originalReturnDate = autoReturnDate;
      }
      
      setDepartureDate(originalDepartureDate);
      setReturnDate(originalReturnDate);
      
      // Set travelDate based on isViewingReturn
      setTravelDate(isViewingReturn ? originalReturnDate : originalDepartureDate);
      
      setInitialized(true);
    }
  }, [initialized, paramDeparture, paramReturn, isViewingReturn]);

  const baseUrl = tripPlannerData.baseUrl;

  // Add this at component level
  const shortTransferStationsRef = useRef(new Map());
  const isRetryWithLongerTransfersRef = useRef(false);

  // Modified fetchTripOptions with all improvements
  useEffect(() => {
    const fetchTripOptions = async (startDate, longerTransferStation = null) => {
      if (requestCountRef.current >= 15) {
        console.log("Too many requests, stopping [1]");
        handleLoadingComplete();
        return;
      }

      // Create new abort controller for this request cycle
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      abortControllerRef.current = new AbortController();

      const source = axios.CancelToken.source();

      try {
        if (!isOnline) {
          throw new Error('No internet connection');
        }

        // Set loading states based on request phase
        if (requestCountRef.current === 0) {
          setLoading(true);
          setRequestPhase('initial');
        } else if (!longerTransferStation) {
          setIsLoadingMore(true);
          setLoading(false);
          setRequestPhase('more');
        } else {
          setIsLoadingMore(true);
          setLoading(false);
          setRequestPhase('extra');
        }

        const originalOrigin = unslugify(origin);
        const originalDestination = unslugify(destination);
        // Modify the cache key to include the station parameter if it exists
        const cacheKey = `${originalOrigin}-${originalDestination}-${startDate}${longerTransferStation ? `-${longerTransferStation}` : ''}`;

        // Check cache first
        const cachedData = tripCacheRef.current.get(cacheKey);
        if (cachedData) {
          console.log("Loading from cache");
          handleTripData(cachedData);
          return;
        }

        // Implement retry logic with exponential backoff
        let retryCount = 0;
        let lastError = null;

        while (retryCount <= MAX_RETRIES) {
          try {
            const response = await requestQueueRef.current.add(async () => {
              // Create a moment object and format it to match the desired string format
              const dateString = moment(startDate)
                .format('ddd MMM DD YYYY HH:mm:ss [GMT]ZZ')
                .replace('GMT+0200', 'GMT+0200');  // Ensure consistent timezone format

              const params = {
                origin: originalOrigin,
                destination: originalDestination,
                date: dateString,
              };

              // Add longer transfer parameter if specified
              if (longerTransferStation) {
                params.longerTransferStation = longerTransferStation;
              }

              const result = await axios.get(`https://traincitytrip.eu/trip-options`, {
                params,
                cancelToken: source.token,
                signal: abortControllerRef.current.signal,
                timeout: 30000,
              });
              return result;
            });

            if (response?.data) {
              // Cache successful response
              tripCacheRef.current.set(cacheKey, response.data);
              
              // If no data is returned or empty array, stop making more requests
              if (!response.data.length) {
                console.log("No data returned, stopping requests");
                handleLoadingComplete();
                return;
              }
              
              // Process the response data
              handleTripData(response.data);
              return; // Exit the function after successful processing
            } else {
              // If no response data, stop making more requests
              console.log("No response data, stopping requests");
              handleLoadingComplete();
              return;
            }

          } catch (error) {
            lastError = error;
            if (axios.isCancel(error) || error.name === 'AbortError') {
              throw error; // Don't retry canceled requests
            }

            if (retryCount === MAX_RETRIES) {
              throw error; // Max retries reached
            }

            // Calculate delay with exponential backoff
            const delay = Math.min(
              BASE_RETRY_DELAY * Math.pow(2, retryCount),
              MAX_RETRY_DELAY
            );

            await new Promise(resolve => setTimeout(resolve, delay));
            retryCount++;
          }
        }

        // If we get here, all retries failed
        throw lastError || new Error('Failed to fetch trip options');

      } catch (error) {
        if (!axios.isCancel(error) && error.name !== 'AbortError') {
          console.error('Request failed:', error);
          handleLoadingComplete();
        }
      }
    };

    // Helper function to process trip data
    const handleTripData = (data) => {
      if (data.length > 0) {
        const firstTrip = data[0];
        const originStation = firstTrip.legs[0].origin;
        const destinationStation = firstTrip.legs[firstTrip.legs.length - 1].destination;

        setNsOrigin(getNSEncoding(originStation));
        setNsDestination(getNSEncoding(destinationStation));

        const lastTrip = data[data.length - 1];
        const lastDepartureTime = moment(lastTrip.departure);
        const inputDate = moment(travelDate, 'DD-MM-YYYY', true);

        // Check if we're getting trips from a different day
        if (!lastDepartureTime.isSame(inputDate, 'day')) {
          // If we're in the extra phase, this means we've completed the current station
          if (requestPhase === 'extra' || isRetryWithLongerTransfersRef.current) {
            console.log("Different day reached in extra phase, moving to next station or completing");
            return;
          }

          if (!isRetryWithLongerTransfersRef.current && shortTransferStationsRef.current.size > 0 && ENABLE_EXTRA_REQUESTS) {
            // Only collect problematic stations during initial and 'more' phases
            if (requestPhase !== 'extra') {
              // Get top 2 problematic stations
              const sortedStations = Array.from(shortTransferStationsRef.current.entries())
                .sort((a, b) => b[1] - a[1])
                .slice(0, 2)
                .map(([station]) => station);

              // Set flag to prevent original API calls
              isRetryWithLongerTransfersRef.current = true;

              // Reset time to start of day and retry with problematic stations
              const startOfDay = moment(inputDate).startOf('day');
              
              // Make requests sequential for each station
              const makeStationRequests = async (stationIndex) => {
                if (stationIndex >= sortedStations.length) {
                  console.log("All stations processed, completing");
                  handleLoadingComplete(); // Move handleLoadingComplete here
                  return;
                }

                const currentStation = sortedStations[stationIndex];
                // Reset the previousLastDepartureTimeRef for each new station
                previousLastDepartureTimeRef.current = null;
                
                const makeNextRequest = async (startTime) => {
                  const lastTime = previousLastDepartureTimeRef.current;
                  await fetchTripOptions(startTime, currentStation);
                  
                  // If previousLastDepartureTimeRef hasn't changed, it means we got cached data
                  // or reached a different day
                  if (lastTime === previousLastDepartureTimeRef.current || 
                      (previousLastDepartureTimeRef.current && 
                       !moment(previousLastDepartureTimeRef.current).isSame(inputDate, 'day'))) {
                    // Move to next station
                    await makeStationRequests(stationIndex + 1);
                    return;
                  }

                  // Continue with the same station if we're still on the same day
                  if (requestCountRef.current < 15) {
                    const nextStartTime = moment(previousLastDepartureTimeRef.current).add(2, 'hours');
                    await makeNextRequest(nextStartTime);
                  } else {
                    // Hit request limit, move to next station
                    await makeStationRequests(stationIndex + 1);
                  }
                };

                // Start the chain of requests for this station
                await makeNextRequest(startOfDay);
              };
              
              // Start with the first station
              makeStationRequests(0);
              return;
            }
          } else {
            // If extra requests are disabled, complete the loading here
            console.log("Loading complete - extra requests disabled");
            handleLoadingComplete();
          }
          return;
        }

        // Only stop if we're getting trips from a previous request
        if (previousLastDepartureTimeRef.current && 
            lastDepartureTime.isSameOrBefore(moment(previousLastDepartureTimeRef.current))) {
            console.log("previousLastDepartureTimeRef.current", previousLastDepartureTimeRef.current);
            console.log("lastDepartureTime", lastDepartureTime);
            console.log("Loading complete - duplicate results");
            return;
        }

        const newTrips = data.filter(trip => {
            const tripKey = `${trip.departure}-${trip.arrival}`;
            if (uniqueTripsRef.current.has(tripKey)) {
              return false;
            }
            uniqueTripsRef.current.add(tripKey);
            trip.complexity = calculateComplexity(trip, shortTransferStationsRef);
            return true;
        });

        setTripOptions(prevOptions => [...prevOptions, ...newTrips]);
        previousLastDepartureTimeRef.current = lastDepartureTime.format();
        requestCountRef.current += 1;

        // Only continue with regular requests if we're not in extra mode
        if (!isRetryWithLongerTransfersRef.current) {
          const nextStartDate = lastDepartureTime.add(1, 'minutes');
          if (nextStartDate && requestCountRef.current < 15) {
            fetchTripOptions(nextStartDate, data.longerTransferStation);
          } else {
            console.log("Loading complete - max requests reached");
            handleLoadingComplete();
          }
        }
      } else {
        // Only complete if we're in the extra phase or if there are no problematic stations
        if (requestPhase === 'extra' || isRetryWithLongerTransfersRef.current || shortTransferStationsRef.current.size === 0) {
          console.log("No trips found, stopping");
          handleLoadingComplete();
        }
      }
    };

    const fetchInitialTrips = async () => {
      if (!travelDate || typeof travelDate === 'object') {
        return;
      }

      setLoading(true);
      tripCacheRef.current.clear(); // Clear cache on new search
      uniqueTripsRef.current.clear();
      requestCountRef.current = 0;

      try {
        const initialDate = moment(travelDate, 'DD-MM-YYYY');
        await fetchTripOptions(initialDate);
      } catch (error) {
        console.error('Error in fetchInitialTrips:', error);
        handleLoadingComplete();
      }
    };

    fetchInitialTrips();

    // Cleanup function
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [origin, destination, travelDate, isOnline]);

  const checkTicketAvailability = useCallback(async (trip) => {
    const compositeKey = createCompositeKey(trip.departure, trip.arrival);
    setLoadingAvailability(prev => ({ ...prev, [compositeKey]: true }));

    try {
      const incompatibleOperators = ['operator1', 'operator2'];
      const dbOnlyOperators = ['FlixTrain', 'European Sleeper'];
      let vendors = [];

      // Determine vendor
      const priceIsKnown = trip.price?.amount !== undefined && trip.price.amount >= 0;
      
      const isTripInNL = nsOrigin.startsWith('NL') || nsDestination.startsWith('NL');

      if (
        isTripInNL && 
        !trip.legs.some(leg => incompatibleOperators.includes((leg.operator).split(',', 1)[0])) &&
        !trip.legs.some(leg => dbOnlyOperators.includes((leg.operator).split(',', 1)[0]))
      ) {
        vendors.push('NS');
      }

      if (priceIsKnown &&
        !trip.legs.some(leg => "European Sleeper".includes((leg.operator).split(',', 1)[0]))
      ) {
        vendors.push('DB');
      }

      if (trip.legs.length === 1 && trip.legs[0].operator === "European Sleeper") {
        vendors.push('European Sleeper');
      }

      let isAvailable = (vendors.length === 0);

      const bookingUrls = [];

      /**
       * Generates the passenger parameter string based on the operator and passenger counts.
       * 
       * @param {Object} passengerCounts - The counts of different passenger categories.
       * @param {string} operator - The name of the operator (e.g., 'NS', 'DB', 'European Sleeper').
       * @returns {string} - The formatted passenger parameter string.
       */
      const generatePaxParameter = (passengerCounts, operator) => {
        const { adults, babies, children4to5, children6to11, children12to14, youth15, youth16to25, seniors } = passengerCounts;
        let pax = [];

        if (operator === 'NS') {
          // NS Parameter Mapping
          if (babies > 0) pax.push(...Array(babies).fill('C_0'));
          if (children4to5 > 0) pax.push(...Array(children4to5).fill('C_4'));
          if (children6to11 > 0) pax.push(...Array(children6to11).fill('C_6'));
          if (children12to14 > 0) pax.push(...Array(children12to14).fill('Y_12'));
          if (youth15 > 0) pax.push(...Array(youth15).fill('Y_15'));
          if (youth16to25 > 0) pax.push(...Array(youth16to25).fill('Y'));
          if (adults > 0) pax.push(...Array(adults).fill('A'));
          if (seniors > 0) pax.push(...Array(seniors).fill('S_65'));

          return pax.join(',');
        } else if (operator === 'European Sleeper') {
          // European Sleeper Parameter Mapping
          let passengerTypes = [];

          // children4to5 and children6to11 map to PassengerTypes=73
          if (children4to5 > 0) {
            passengerTypes.push(...Array(children4to5).fill('73'));
          }
          if (children6to11 > 0) {
            passengerTypes.push(...Array(children6to11).fill('73'));
          }

          // children12to14, youth15, youth16to25, adults, and seniors map to PassengerTypes=72
          const totalAdults = children12to14 + youth15 + youth16to25 + adults + seniors;
          if (totalAdults > 0) {
            passengerTypes.push(...Array(totalAdults).fill('72'));
          }

          // Construct the passengerTypes query parameters
          return passengerTypes.map(type => `passengerTypes=${type}`).join('&');
        } else if (operator === 'DB') {
          let dbPax = [];
          
          // Adults (27-64)
          if (adults > 0) {
            dbPax.push(`13:16:KLASSENLOS:${adults}`);
          }
          
          // Seniors (65+)
          if (seniors > 0) {
            dbPax.push(`12:16:KLASSENLOS:${seniors}`);
          }
          
          // Youth (15-26)
          const totalYouth = youth15 + youth16to25;
          if (totalYouth > 0) {
            dbPax.push(`9:16:KLASSENLOS:${totalYouth}`);
          }
          
          // Children (6-14)
          const totalChildren6to14 = children6to11 + children12to14;
          if (totalChildren6to14 > 0) {
            dbPax.push(`11:16:KLASSENLOS:${totalChildren6to14}`);
          }
          
          // Children (0-5)
          const totalChildren0to5 = babies + children4to5;
          if (totalChildren0to5 > 0) {
            dbPax.push(`8:16:KLASSENLOS:${totalChildren0to5}`);
          }
          
          return `r=${dbPax.join(',')}`;
        }

        // Default fallback if operator is unknown
        console.warn(`Unknown operator "${operator}". Returning empty passenger parameter.`);
        return '';
      };

      if (vendors.includes('NS')) {
        if (nsOrigin === "NS-MAPPING-ERROR" || nsDestination === "NS-MAPPING-ERROR") {
          // Instead of setting isAvailable to false, we'll just filter out NS from vendors
          vendors = vendors.filter(v => v !== 'NS');
        } else {
          const formattedDate = moment(travelDate, 'DD-MM-YYYY').format('YYYYMMDD');
          const formattedDepartureTime = format(new Date(trip.departure), 'HHmm');
          const formattedArrivalTime = format(new Date(trip.arrival), 'HHmm');

          const paxParameter = generatePaxParameter(passengerCounts, 'NS');
          const directBookingUrl = `https://www.nsinternational.com/nl/treintickets-v3/#/search/${nsOrigin}/${nsDestination}/${formattedDate}/${formattedDepartureTime}/${formattedArrivalTime}?pax=${paxParameter}`;
          const ttBase = "https://www.nsinternational.com/traintracker/"
          const refCode = "943_35043_456543_";
          bookingUrls['NS'] = `${ttBase}?tt=${refCode}&r=${encodeURIComponent(directBookingUrl)}`;
        }
      }
      
      if (vendors.includes('DB')) {
        const formattedDate = format(new Date(trip.departure), 'yyyy-MM-dd');
        const formattedTime = format(new Date(trip.departure), 'HH:mm:ss');
        const encodedOrigin = encodeURIComponent(trip.legs[0].origin);
        const encodedDestination = encodeURIComponent(trip.legs[trip.legs.length - 1].destination);

        // Calculate shortest transfer time
        const shortestTransfer = trip.legs.length > 1 
          ? Math.min(...trip.legs.slice(1).map((leg, index) => {
              const prevLeg = trip.legs[index];
              return Math.floor((new Date(leg.departure) - new Date(prevLeg.arrival)) / (1000 * 60)); // Convert to minutes
            }))
          : 0;

        // Round down to nearest valid transfer time
        const validTransferTime = getValidTransferTime(shortestTransfer);

        const awId = "1693255";
        let dbDirectBookingUrl = `https%3A%2F%2Fint.bahn.de%2Fen%2Fbuchung%2Fstart%3Flang%3Dnl%26sts%3Dfalse%26so%3D${encodedOrigin}%26zo%3D${encodedDestination}%26hd%3D${formattedDate}T${formattedTime}`;

        // Add transfer station parameter if 'via' field is present
        if (trip.via) {
          // Find actual transfer stations in the trip
          const transferStations = trip.legs.slice(1).map(leg => leg.origin);
          
          // Find if there's a similar station to the suggested 'via' station
          let actualTransferStation = trip.via;
          if (!transferStations.includes(trip.via)) {
            // First normalize station names by adding spaces around parentheses and standardizing format
            const normalizeStation = (station) => {
              return station
                .replace(/\(/g, ' (')
                .replace(/\)/g, ') ')
                .replace(/\s+/g, ' ')  // Replace multiple spaces with single space
                .replace(/Hbf/g, 'Hauptbahnhof')  // Standardize Hbf to Hauptbahnhof
                .trim();
            };

            const normalizedVia = normalizeStation(trip.via);
            const normalizedTransferStations = transferStations.map(normalizeStation);
            
            // Try to find a similar station by comparing first word (city name)
            const similarStation = transferStations.find((station, index) => 
              normalizedVia.split(' ')[0] === normalizedTransferStations[index].split(' ')[0] // Compare first word (e.g., "Frankfurt")
            );
            
            if (similarStation) {
              actualTransferStation = similarStation;
            }
          }

          // Create the transfer station parameter with the actual transfer station
          const transferStation = encodeURIComponent(`[["A=1@O=${actualTransferStation}@X=8663785@Y=50107149@U=80@L=8000105@B=1@p=1738178088@i=U×008011068@","${actualTransferStation}",35]]`);
          dbDirectBookingUrl += `%26hz%3D${transferStation}`;
        }

        // Add minimum transfer duration parameter if there are transfers
        if (shortestTransfer > 0) {
          dbDirectBookingUrl += `%26mud%3D${validTransferTime}`;
        }

        // Add remaining parameters
        dbDirectBookingUrl += `%26${generatePaxParameter(passengerCounts, 'DB')}%26dbkanal_003%3DL01_S01_D001_KAF0001_14964_Linkgenerator-individuelle-Verbindungen-INT-${awId}_LZ03`;

        const awBase = `https://www.awin1.com/cread.php?awinmid=14964&awinaffid=${awId}&linkid=3451566`;
        bookingUrls['DB'] = `${awBase}&p=${encodeURIComponent(dbDirectBookingUrl)}`;
      }

      // Filter vendors when 'via' field is present
      if (trip.via) {
        vendors = vendors.filter(vendor => vendor === 'DB');
        // Also filter bookingUrls to only keep DB
        Object.keys(bookingUrls).forEach(key => {
          if (key !== 'DB') {
            delete bookingUrls[key];
          }
        });
      }

      if (vendors.includes('European Sleeper')) {
        if (trip['booking-page']) {  // Only proceed if booking-page exists
          const directBookingUrl = `https://www.europeansleeper.eu/train/`;
          const refCode = "37347_2209752_456543_";
          
          // Replace the default passenger types with the generated ones
          let modifiedBookingPage = trip['booking-page'].replace(
            /passengerTypes=72/g,  // Replace all occurrences
            generatePaxParameter(passengerCounts, 'European Sleeper')
          );
          
          const bookingPage = encodeURIComponent(modifiedBookingPage);
          bookingUrls['European Sleeper'] = `${directBookingUrl}?tt=${refCode}&r=${bookingPage}`;
        }
        // If booking-page is undefined, bookingUrls['European Sleeper'] will not be set
      }

      setAvailableTrips(prev => ({
          ...prev,
          [compositeKey]: {
              isAvailable,
              vendors,
              bookingUrls
          }
      }));
    } catch (error) {
      console.error('Error checking ticket availability:', error);
      setAvailableTrips(prev => ({ ...prev, [compositeKey]: { isAvailable: false, vendors: [], bookingUrls: [] } }));
    } finally {
      setLoadingAvailability(prev => ({ ...prev, [compositeKey]: false }));
    }
  }, [nsOrigin, nsDestination, passengerCounts, travelDate]);

  useEffect(() => {
    if (tripOptions.length > 0) {
      tripOptions.forEach((trip) => {
        checkTicketAvailability(trip);
      });
    }
  }, [tripOptions, checkTicketAvailability]);

  useEffect(() => {
    if (loading) return;
  
    const adjustLegDetails = () => {
      const measureWidth = () => {
        if (journeyRef.current) {
          setJourneyWidthInPixels(journeyRef.current.offsetWidth);
        }
      };

      measureWidth();
      setIsMobile(window.innerWidth <= 767);
      const journeyLegs = document.querySelectorAll('.journey-leg');
  
      journeyLegs.forEach(journeyLeg => {
        const legDetails = journeyLeg.querySelector('.leg-details');
        const dashedLine = journeyLeg.querySelector('.dashed-line');
  
        if (legDetails && dashedLine) {
          const isWalking = legDetails.querySelector('.walking-icon');
          if (isWalking) return;
  
          legDetails.classList.remove('hidden', 'hide-text');
  
          const legDetailsWidth = legDetails.offsetWidth;
          const dashedLineWidth = dashedLine.offsetWidth;
  
          if (!isMobile) {
            if (legDetailsWidth > 0.95 * dashedLineWidth) {
              legDetails.classList.add('hidden');
              legDetails.classList.remove('hide-text');
            } else if (legDetailsWidth > 0.75 * dashedLineWidth) {
              legDetails.classList.add('hide-text');
              legDetails.classList.remove('hidden');
            } else {
              legDetails.classList.remove('hidden', 'hide-text');
            }
          } else {
            legDetails.classList.remove('hidden', 'hide-text'); /* Mobile styles */
          }
        }
      });
    };
  
    adjustLegDetails();
    window.addEventListener('resize', adjustLegDetails);
  
    return () => {
      window.removeEventListener('resize', adjustLegDetails);
    };
  }, [loading, tripOptions, isMobile]);

  const createCompositeKey = (departure, arrival) => {
    return `${departure}_${arrival}`;
  };

  // Modify the scrollToSearchRef function
  const scrollToSearchRef = () => {
    // Only scroll if this is the initial load (no results yet)
    if (tripOptions.length === 0 && scrollRef.current) {
      const topPosition = scrollRef.current.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        top: topPosition,
        behavior: "smooth",
      });
    }
  };

  const resetState = () => {
    setLoading(true);
    setInitialized(false);
    previousLastDepartureTimeRef.current = null;
    setTripOptions([]);
    requestCountRef.current = 0;
    uniqueTripsRef.current = new Set();

    scrollToSearchRef();
  };

  // Function to reset state and clear saved trips when search parameters change
  const resetStateAndClearSavedTrips = () => {
    resetState();
    clearSavedTrips();
  };

  // Function to save current trip as outbound trip
  const saveOutboundTrip = (trip) => {
    setSavedOutboundTrip(trip);
    setIsShowingSavedTripsOnly(false);
  };

  // Function to save current trip as return trip
  const saveReturnTrip = (trip) => {
    setSavedReturnTrip(trip);
    setIsShowingSavedTripsOnly(true);
  };

  // Function to clear saved trips
  const clearSavedTrips = () => {
    setSavedOutboundTrip(null);
    setSavedReturnTrip(null);
    setIsShowingSavedTripsOnly(false);
  };

  // Function to show only saved trips
  const showSavedTripsOnly = () => {
    setIsShowingSavedTripsOnly(true);
  };

  const captureSavedTripsScreenshot = async () => {
    if (!savedTripsContainerRef.current) {
      console.error('Saved trips container not found');
      return;
    }

    try {
      const canvas = await html2canvas(savedTripsContainerRef.current, {
        backgroundColor: '#ffffff',
        scale: 2, // Higher quality
        useCORS: true,
        allowTaint: true,
        scrollX: 0,
        scrollY: 0,
        width: savedTripsContainerRef.current.scrollWidth,
        height: savedTripsContainerRef.current.scrollHeight
      });

      // Convert canvas to blob and download
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `opgeslagen-reizen-${new Date().toISOString().split('T')[0]}.png`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }, 'image/png');
    } catch (error) {
      console.error('Error capturing screenshot:', error);
      alert('Er is een fout opgetreden bij het maken van de screenshot.');
    }
  };

  // Function to slugify and store the mapping
  function slugifyWithMapping(text) {
    const slugified = text
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .toLowerCase();

    // Store the mapping
    stationNameMapping[slugified] = text;
    return slugified;
  }

  // Define handlers to update state based on selected city
  const handleOriginChange = (selectedOption) => {
    if (selectedOption) {
      setIsViewingReturn(false);  // Reset to outbound journey view
      setOrigin(selectedOption.value);
      setTravelDate(departureDate);

      const slugifiedOrigin = slugifyWithMapping(selectedOption.value);
      const slugifiedDestination = slugifyWithMapping(destination);
      const slugifiedDeparture = slugify(departureDate);
      const slugifiedReturn = slugify(returnDate);

      resetStateAndClearSavedTrips();
      navigate(`/trip-planner/${slugifiedOrigin}/${slugifiedDestination}/${slugifiedDeparture}/${slugifiedReturn}`);
    }
  };

  const handleDestinationChange = (selectedOption) => {
    if (selectedOption) {
      setIsViewingReturn(false);  // Reset to outbound journey view
      setDestination(selectedOption.value);
      setTravelDate(departureDate);

      const slugifiedOrigin = slugifyWithMapping(origin);
      const slugifiedDestination = slugifyWithMapping(selectedOption.value);
      const slugifiedDeparture = slugify(departureDate);
      const slugifiedReturn = slugify(returnDate);

      resetStateAndClearSavedTrips();
      navigate(`/trip-planner/${slugifiedOrigin}/${slugifiedDestination}/${slugifiedDeparture}/${slugifiedReturn}`);
    }
  };

  const handleDateDelta = (isReturn, daysDelta, fromNow = false) => {
    let baseDate;

    if (isReturn) {
      baseDate = fromNow ? new Date() : moment(returnDate, 'DD-MM-YYYY').toDate();
      const newDate = new Date(baseDate);
      newDate.setDate(newDate.getDate() + daysDelta);
      handleDateChange(newDate, true);
    } else {
      baseDate = fromNow ? new Date() : moment(departureDate, 'DD-MM-YYYY').toDate();
      const newDate = new Date(baseDate);
      newDate.setDate(newDate.getDate() + daysDelta);
      handleDateChange(newDate, false);
    }
  };

  const handleDateChange = (date, isReturn = false) => {
    if (date) {
      // Ensure we're working with a Date object
      const dateObj = date instanceof Date ? date : new Date(date);
      if (isNaN(dateObj.getTime())) {
        console.error("Invalid date:", date);
        return;
      }

      // Format the new date
      const newFormattedDate = format(dateObj, 'dd-MM-yyyy');

      // Determine if the currently viewed date is being changed
      const isCurrentView = (isReturn && isViewingReturn) || (!isReturn && !isViewingReturn);

      // Update the appropriate date state
      if (isReturn) {
        setReturnDate(newFormattedDate);
      } else {
        setDepartureDate(newFormattedDate);
      }

      // Determine the new departure and return dates for navigation
      const updatedDepartureDate = isReturn ? departureDate : newFormattedDate;
      const updatedReturnDate = isReturn ? newFormattedDate : returnDate;

      // Slugify all dynamic segments using the updated dates
      const slugifiedOrigin = slugify(origin);
      const slugifiedDestination = slugify(destination);
      const slugifiedDeparture = slugify(updatedDepartureDate);
      const slugifiedReturn = slugify(updatedReturnDate);

      // Navigate using the updated dates
      navigate(`/trip-planner/${slugifiedOrigin}/${slugifiedDestination}/${slugifiedDeparture}/${slugifiedReturn}`);

      // Clear saved trips based on which date is being changed
      if (isReturn) {
        // Only clear saved return trip when return date changes
        setSavedReturnTrip(null);
      } else {
        // Only clear saved outbound trip when departure date changes
        setSavedOutboundTrip(null);
      }
      
      // If the current view is active, update travelDate and reset state
      if (isCurrentView) {
        setTravelDate(newFormattedDate);
        resetState();
      }
    } else {
      console.error("Issue setting new date: {date}.");
    }
  };

  const handleDateDisplayClick = (ref) => {
    if (ref.current) {
      ref.current.setFocus();
    }
  };

  const handleSwapLocations = (clearSavedTrips = true) => {
    // Get the current values from the CityPicker state
    const tempOrigin = origin;
    const tempDestination = destination;
    
    // Simply swap the values in state without any URL navigation
    setOrigin(tempDestination);
    setDestination(tempOrigin);
    
    if (clearSavedTrips) {
      resetStateAndClearSavedTrips(); // Reset state to fetch new trip options
    } else {
      resetState(); // Only reset trip options, don't clear saved trips
    }
  };

  // Function to update passenger counts
  const updatePassengerCount = (category, delta) => {
    setPassengerCounts(prev => {
      const newCounts = {
        ...prev,
        [category]: Math.max(0, Math.min(9, prev[category] + delta)) // Ensure count doesn't go below 0
      };
      setHasPassengerBeenModified(true);
      return newCounts;
    });
  };

  // Function to calculate similarity between two strings
  const similarity = (s1, s2) => {
    let longer = s1;
    let shorter = s2;
    if (s1.length < s2.length) {
      longer = s2;
      shorter = s1;
    }
    const longerLength = longer.length;
    if (longerLength === 0) {
      return 1.0;
    }
    return (longerLength - editDistance(longer, shorter)) / parseFloat(longerLength);
  };

  // Function to determine if a location matches any of the specified locations
  const isFuzzyMatch = (location, targets) => {
    return targets.some(target => similarity(location.toLowerCase(), target.toLowerCase()) > 0.8);
  };

  // Function to get highlighted dates based on origin and destination
  const getHighlightedDates = (origin, destination) => {
    const highlightDates = [];
    const targetLocations = ['BrenneroBrenner', 'bressanone', 'Ponte GardenaWaidbruck', 'BolzanoBozen', 'Verona', 'Verona Porta Nuova', 'Venezia', 'Venetië Mestre', 'Venetië Santa Lucia'];

    if (isFuzzyMatch(destination, targetLocations)) {
      highlightDates.push(parseDate('05-02-2025'), parseDate('14-02-2025'), parseDate('04-03-2025'), parseDate('12-03-2025'));
    }

    if (isFuzzyMatch(origin, targetLocations)) {
      highlightDates.push(parseDate('09-02-2025'), parseDate('18-02-2025'), parseDate('08-03-2025'), parseDate('16-03-2025'));
    }

    return highlightDates;
  };

  // Function to determine the class for each day
  const getDayClassName = (date, selectedDate, highlightDates) => {
    if (!selectedDate) return ''; // Return empty string if selectedDate is null or undefined

    const selectedDateObj = moment(selectedDate, 'DD-MM-YYYY').toDate(); // Ensure selectedDate is a Date object

    const isSameMonthAndYear = date.getMonth() === selectedDateObj.getMonth() &&
                               date.getFullYear() === selectedDateObj.getFullYear();

    const isSelectedDate = date.getDate() === selectedDateObj.getDate() && isSameMonthAndYear;

    // Check if the date is part of highlightDates
    const isHighlighted = highlightDates.some(highlightDate => {
      const highlightDateObj = moment(highlightDate, 'DD-MM-YYYY').toDate();
      return date.getTime() === highlightDateObj.getTime();
    });

    // Apply styling for highlightDates with priority
    if (isHighlighted) {
      return 'highlight-date';
    }

    // Apply styling only if the date is the selected date of the selected month
    if (isSelectedDate) {
      return 'highlight-selected-date';
    }

    return '';
  };

  // Helper function to get complexity label
  const getComplexityLabel = (value) => {
    switch (value) {
      case 1:
        return "Geschikt voor beginners";
      case 2:
        return "Iets moeilijkere reis";
      case 3:
        return "Complexe reis";
      default:
        return "Geschikt voor beginners";
    }
  };

  // Add these helper functions if they're not already defined elsewhere in the file
  const formatDuration = (trip) => {
    const duration = (new Date(trip.arrival) - new Date(trip.departure)) / (1000 * 60);
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.complexity-clickable')) {
        setMobileTooltipVisible(false);
      }
    };

    const handleScroll = () => {
      setMobileTooltipVisible(false);
    };

    document.addEventListener('click', handleClickOutside);
    window.addEventListener('scroll', handleScroll);

    return () => {
      document.removeEventListener('click', handleClickOutside);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Add this new helper function
  const handleLoadingComplete = () => {
    setLoadingState('complete');
    setTimeout(() => {
      setLoadingState('hiding');
      setTimeout(() => {
        setLoadingState('hidden');
        setLoading(false);
        setIsLoadingMore(false);
      }, 200); // Reduced from 400ms to 200ms
    }, 3000);
  };

  // Calculate shortest transfer time and round down to nearest allowed value
  const getValidTransferTime = (minutes) => {
    const allowedValues = [10, 15, 20, 25, 30, 35, 40, 45];
    // Find the largest allowed value that is less than or equal to the actual transfer time
    return allowedValues.reverse().find(value => value <= minutes) || 10; // Default to 10 if transfer time is < 10
  };

  const [isPassengerExpanded, setIsPassengerExpanded] = useState(false);
  const [hasPassengerBeenModified, setHasPassengerBeenModified] = useState(false);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);
  
  // Filter serialization/deserialization utilities
  const serializeFilters = (filters) => {
    try {
      // Validate filters object
      if (!filters || typeof filters !== 'object') {
        console.error('Invalid filters object for serialization');
        return null;
      }
      
      const filterString = JSON.stringify(filters);
      return btoa(encodeURIComponent(filterString));
    } catch (error) {
      console.error('Error serializing filters:', error);
      return null;
    }
  };

  const deserializeFilters = (base64String) => {
    try {
      // Validate base64 string
      if (!base64String || typeof base64String !== 'string') {
        console.error('Invalid base64 string for deserialization');
        return null;
      }
      
      const filterString = decodeURIComponent(atob(base64String));
      const filters = JSON.parse(filterString);
      
      // Validate the parsed filters object
      if (!filters || typeof filters !== 'object') {
        console.error('Invalid filters object after deserialization');
        return null;
      }
      
      return filters;
    } catch (error) {
      console.error('Error deserializing filters:', error);
      return null;
    }
  };

  const removeFiltersFromURL = () => {
    const currentURL = new URL(window.location);
    currentURL.searchParams.delete('filters');
    window.history.replaceState({}, '', currentURL.toString());
  };

  const areFiltersDefault = (filters) => {
    // Check if any filter tags would be shown (same logic as in the UI)
    // This ensures perfect consistency between visible filter tags and URL behavior
    
    // Journey type filter
    if (filters.journeyType !== 'all') {
      return false;
    }
    
    // Departure time range filter
    if (filters.departureTimeRange[0] !== 0 || filters.departureTimeRange[1] !== 24) {
      return false;
    }
    
    // Arrival time range filter
    if (filters.arrivalTimeRange[0] !== 0 || filters.arrivalTimeRange[1] !== 24) {
      return false;
    }
    
    // Max transfers filter
    if (filters.maxTransfers !== 9) {
      return false;
    }
    
    // Operators filter
    if (Object.entries(filters.operators).some(([_, selected]) => !selected)) {
      return false;
    }
    
    // Vendors filter
    if (Object.entries(filters.vendors).some(([_, selected]) => !selected)) {
      return false;
    }
    
    // Via station filter
    if (filters.viaStation) {
      return false;
    }
    
    // Complexity filter
    if (filters.complexity !== '3') {
      return false;
    }
    
    // Train types filter
    if (Object.values(filters.trainTypes).some(value => !value)) {
      return false;
    }
    
    return true;
  };

  const updateURLWithFilters = (filters) => {
    // Only add filters to URL if they differ from defaults
    if (areFiltersDefault(filters)) {
      removeFiltersFromURL();
    } else {
      const serializedFilters = serializeFilters(filters);
      if (serializedFilters) {
        const currentURL = new URL(window.location);
        currentURL.searchParams.set('filters', serializedFilters);
        window.history.replaceState({}, '', currentURL.toString());
      }
    }
  };

  const getFiltersFromURL = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const filtersParam = urlParams.get('filters');
    if (filtersParam) {
      return deserializeFilters(filtersParam);
    }
    return null;
  };

  const [filters, setFilters] = useState(() => {
    // Try to load filters from URL first, otherwise use defaults
    const urlFilters = getFiltersFromURL();
    if (urlFilters) {
      return urlFilters;
    }
    
    return {
      departureTimeRange: [0, 24],
      arrivalTimeRange: [0, 24],
      maxTransfers: 9,
      vendors: {
        'NS': true,
        'DB': true,
        'European Sleeper': true
      },
      operators: {},
      viaStation: '',
      complexity: '3',
      trainTypes: {
        regional: true,
        national: true,
        regionalExpress: true,
        nationalExpress: true,
        ferry: true,
        suburban: true,
        'N/A': true,
        nighttrain: true
      },
      journeyType: 'all' // Add new filter
    };
  });

  // Handle URL changes (browser back/forward navigation)
  useEffect(() => {
    const handlePopState = () => {
      const urlFilters = getFiltersFromURL();
      if (urlFilters) {
        setFilters(urlFilters);
      } else {
        // URL has no filters, reset to defaults
        setFilters(prev => ({
          departureTimeRange: [0, 24],
          arrivalTimeRange: [0, 24],
          maxTransfers: 9,
          vendors: {
            'NS': true,
            'DB': true,
            'European Sleeper': true
          },
          operators: prev.operators, // Keep existing operators to avoid empty object issue
          viaStation: '',
          complexity: '3',
          trainTypes: {
            regional: true,
            national: true,
            regionalExpress: true,
            nationalExpress: true,
            ferry: true,
            suburban: true,
            'N/A': true,
            nighttrain: true
          },
          journeyType: 'all'
        }));
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []); // Remove uniqueOperators dependency

  // Add state for unique operators
  const [uniqueOperators, setUniqueOperators] = useState(new Set());

  // Add effect to collect unique operators from trips
  useEffect(() => {
    if (!loading && tripOptions.length > 0) {
      const operators = new Set();
      tripOptions.forEach(trip => {
        trip.legs.forEach(leg => {
          const operator = leg.operator.split(',')[0];
          // Group DB operators
          if (operator.toLowerCase().startsWith('db')) {
            operators.add('Deutsche Bahn');
          } else {
            operators.add(operator);
          }
        });
      });

      setUniqueOperators(operators);
      
      // Initialize operator filters if not already set
      setFilters(prev => {
        const currentOperators = prev.operators;
        const newOperators = {};
        
        // If we have operators from URL, use them; otherwise default to true
        operators.forEach(op => {
          if (currentOperators[op] !== undefined) {
            // Keep existing setting from URL
            newOperators[op] = currentOperators[op];
          } else {
            // Default to true for new operators
            newOperators[op] = true;
          }
        });
        
        const newFilters = { ...prev, operators: newOperators };
        
        // Update URL with new filters (only if operators were actually initialized)
        if (Object.keys(currentOperators).length === 0) {
          updateURLWithFilters(newFilters);
        }
        
        return newFilters;
      });
    }
  }, [loading, tripOptions]);

  const handleResetFilters = () => {
    const resetFilters = {
      departureTimeRange: [0, 24],
      arrivalTimeRange: [0, 24],
      maxTransfers: 9,
      vendors: {
        'NS': true,
        'DB': true,
        'European Sleeper': true
      },
      operators: Object.fromEntries([...uniqueOperators].map(op => [op, true])),
      viaStation: '',
      complexity: '3',
      trainTypes: {
        regional: true,
        national: true,
        regionalExpress: true,
        nationalExpress: true,
        ferry: true,
        suburban: true,
        'N/A': true,
        nighttrain: true
      },
      journeyType: 'all' // Add new filter
    };
    
    setFilters(resetFilters);
    // Remove filters from URL since we're resetting to defaults
    removeFiltersFromURL();
  };

  // Add refs for the range tracks
  const departureRangeTrackRef = useRef(null);
  const arrivalRangeTrackRef = useRef(null);

  // Add function to update range track
  const updateRangeTrack = (min, max, trackRef) => {
    if (trackRef.current) {
      const minPercent = (min / 24) * 100;
      const maxPercent = (max / 24) * 100;
      const trackWidth = maxPercent - minPercent;
      trackRef.current.style.left = `${minPercent}%`;
      trackRef.current.style.width = `${trackWidth}%`;
    }
  };

  // Update handleFilterChange to handle range updates
  const handleFilterChange = (filterName, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [filterName]: value };
      
      // Update range track colors
      if (filterName === 'departureTimeRange') {
        const [min, max] = value;
        const minPercent = min / 24;
        const maxPercent = max / 24;
        const slider = document.querySelector('.time-range-slider:nth-of-type(1)');
        if (slider) {
          slider.style.setProperty('--min', minPercent);
          slider.style.setProperty('--max', maxPercent);
        }
      } else if (filterName === 'arrivalTimeRange') {
        const [min, max] = value;
        const minPercent = min / 24;
        const maxPercent = max / 24;
        const slider = document.querySelector('.time-range-slider:nth-of-type(2)');
        if (slider) {
          slider.style.setProperty('--min', minPercent);
          slider.style.setProperty('--max', maxPercent);
        }
      }
      
      // Update URL with new filters
      updateURLWithFilters(newFilters);
      
      return newFilters;
    });
  };

  // Add useEffect to initialize range tracks
  useEffect(() => {
    // Initialize departure time range
    const departureSlider = document.querySelector('.time-range-slider:nth-of-type(1)');
    if (departureSlider) {
      const min = filters.departureTimeRange[0] / 24;
      const max = filters.departureTimeRange[1] / 24;
      departureSlider.style.setProperty('--min', min);
      departureSlider.style.setProperty('--max', max);
    }

    // Initialize arrival time range
    const arrivalSlider = document.querySelector('.time-range-slider:nth-of-type(2)');
    if (arrivalSlider) {
      const min = filters.arrivalTimeRange[0] / 24;
      const max = filters.arrivalTimeRange[1] / 24;
      arrivalSlider.style.setProperty('--min', min);
      arrivalSlider.style.setProperty('--max', max);
    }
  }, [filters.departureTimeRange, filters.arrivalTimeRange]);

  const filterTrips = (trips) => {
    const filteredTrips = trips.filter(trip => {
      // Filter by journey type
      if (filters.journeyType !== 'all') {
        const isNextDayArrival = new Date(trip.arrival).getDate() !== new Date(trip.departure).getDate();
        const hasNightTrain = trip.legs.some(leg => 
          leg.train_number?.startsWith('NJ ') || 
          (leg.operator).split(',', 1)[0] === "European Sleeper" || 
          (leg.operator).split(',', 1)[0] === "Urlaubs-Express"
        );

        if (filters.journeyType === 'night') {
          // For night journeys, require both night train and next day arrival
          if (!hasNightTrain || !isNextDayArrival) {
            return false;
          }
        } else if (filters.journeyType === 'day') {
          // For day journeys, exclude trips that have both night train and next day arrival
          if (hasNightTrain && isNextDayArrival) {
            return false;
          }
        }
      }

      // Filter by departure time
      const departureHour = new Date(trip.departure).getHours();
      if (departureHour < filters.departureTimeRange[0] || departureHour > filters.departureTimeRange[1]) {
        return false;
      }

      // Filter by arrival time
      const arrivalHour = new Date(trip.arrival).getHours();
      if (arrivalHour < filters.arrivalTimeRange[0] || arrivalHour > filters.arrivalTimeRange[1]) {
        return false;
      }

      // Filter by number of transfers
      if (trip.legs.length - 1 > filters.maxTransfers) {
        return false;
      }

      // Filter by vendors
      const tripVendors = availableTrips[createCompositeKey(trip.departure, trip.arrival)]?.vendors || [];
      const hasSelectedVendor = tripVendors.some(vendor => filters.vendors[vendor]);
      // Allow trips with no vendors (international trips that can't be booked through available vendors)
      if (tripVendors.length > 0 && !hasSelectedVendor) {
        return false;
      }

      // Filter by operators - hide trips that contain any deselected operators
      const hasDeselectedOperator = trip.legs.some(leg => {
        const operator = leg.operator.split(',')[0];
        // Check if it's a DB operator
        if (operator.toLowerCase().startsWith('db')) {
          return !filters.operators['Deutsche Bahn'];
        }
        // Check other operators
        return !filters.operators[operator];
      });
      if (hasDeselectedOperator) {
        return false;
      }

      // Filter by via station
      if (filters.viaStation) {
        const normalizedViaStation = filters.viaStation.toLowerCase().trim();
        const hasStation = trip.legs.some((leg, index) => {
          if (index === 0) {
            return leg.origin.toLowerCase().includes(normalizedViaStation);
          }
          return leg.origin.toLowerCase().includes(normalizedViaStation) || 
                 (index === trip.legs.length - 1 && leg.destination.toLowerCase().includes(normalizedViaStation));
        });
        if (!hasStation) {
          return false;
        }
      }

      // Filter by complexity
      if (filters.complexity !== 'all') {
        const complexityValue = trip.complexity?.value || 1;
        const selectedComplexity = parseInt(filters.complexity);
        if (complexityValue > selectedComplexity) {
          return false;
        }
      }

      // Filter by train types
      const hasDeselectedTrainType = trip.legs.some(leg => {
        // Check if it's a nighttrain
        const isNighttrain = leg.train_number?.startsWith('NJ ') || 
                            (leg.operator).split(',', 1)[0] === "European Sleeper" || 
                            (leg.operator).split(',', 1)[0] === "Urlaubs-Express";
        
        if (isNighttrain && !filters.trainTypes.nighttrain) {
          return true; // Exclude if nighttrain is deselected
        }
        
        // Check other train types
        return !filters.trainTypes[leg.product];
      });
      
      if (hasDeselectedTrainType) {
        return false;
      }

      return true;
    });
    return filteredTrips;
  };

  const TimeRangeSelector = ({ label, value, onChange }) => {
    const [min, max] = value;
    const trackRef = useRef(null);
    const overlayRef = useRef(null);
    const [localMin, setLocalMin] = useState(min);
    const [localMax, setLocalMax] = useState(max);
    const MIN_GAP = 1; // Minimum 1 hour gap between handles

    const updateTrack = useCallback(() => {
      if (trackRef.current && overlayRef.current) {
        const minPercent = (localMin / 24) * 100;
        const maxPercent = (localMax / 24) * 100;
        overlayRef.current.style.left = `${minPercent}%`;
        overlayRef.current.style.width = `${maxPercent - minPercent}%`;
      }
    }, [localMin, localMax]);

    useEffect(() => {
      updateTrack();
    }, [updateTrack]);

    useEffect(() => {
      setLocalMin(min);
      setLocalMax(max);
    }, [min, max]);

    const handleChange = (e, isMin) => {
      const newValue = parseInt(e.target.value);
      
      if (isMin) {
        // Prevent min handle from moving beyond (max - MIN_GAP)
        const maxAllowedValue = localMax - MIN_GAP;
        setLocalMin(Math.min(newValue, maxAllowedValue));
      } else {
        // Prevent max handle from moving below (min + MIN_GAP)
        const minAllowedValue = localMin + MIN_GAP;
        setLocalMax(Math.max(newValue, minAllowedValue));
      }
    };

    const handleChangeComplete = (isMin) => {
      if (isMin) {
        // Ensure min value doesn't exceed (max - MIN_GAP)
        const finalMin = Math.min(localMin, localMax - MIN_GAP);
        onChange([finalMin, localMax]);
      } else {
        // Ensure max value doesn't fall below (min + MIN_GAP)
        const finalMax = Math.max(localMax, localMin + MIN_GAP);
        onChange([localMin, finalMax]);
      }
    };

    return (
      <div className="filter-section">
        <h4>{label}</h4>
        <div className="time-range-slider">
          <div className="range-label">
            {localMin}:00 - {localMax}:00
          </div>
          <div className="track-container" ref={trackRef}>
            <div className="track-overlay" ref={overlayRef} />
            <input
              type="range"
              min="0"
              max="24"
              value={localMin}
              onChange={(e) => handleChange(e, true)}
              onMouseUp={() => handleChangeComplete(true)}
              onTouchEnd={() => handleChangeComplete(true)}
            />
            <input
              type="range"
              min="0"
              max="24"
              value={localMax}
              onChange={(e) => handleChange(e, false)}
              onMouseUp={() => handleChangeComplete(false)}
              onTouchEnd={() => handleChangeComplete(false)}
            />
          </div>
        </div>
      </div>
    );
  };

  // Add this helper function to get operator logo
  const getOperatorLogo = (operator) => {
    if (operator === "Nederlandse Spoorwegen") {
      return "nederlandsespoorwegen";
    } else if (operator === "Deutsche Bahn") {
      return "dbfernverkehrag";
    } else if (operator === "European Sleeper") {
      return "europeansleeper";
    } else if (operator === "SNCF") {
      return "sncf";
    } else if (operator === "FlixTrain") {
      return "flixtrain";
    }
    return operator.replace(/\s+/g, '').toLowerCase();
  };

  // Add state for unique stations
  const [uniqueStations, setUniqueStations] = useState(new Set());
  const [stationSuggestions, setStationSuggestions] = useState([]);

  // Add effect to collect unique stations from trips
  useEffect(() => {
    if (!loading && tripOptions.length > 0) {
      const stations = new Set();
      tripOptions.forEach(trip => {
        trip.legs.forEach(leg => {
          stations.add(leg.origin);
          stations.add(leg.destination);
        });
      });
      setUniqueStations(stations);
    }
  }, [loading, tripOptions]);

  // Add function to handle station input
  const handleStationInput = (value) => {
    if (!value.trim()) {
      setStationSuggestions([]);
      handleFilterChange('viaStation', '');
      return;
    }

    const normalizedInput = value.toLowerCase().trim();
    const suggestions = [...uniqueStations]
      .filter(station => 
        station.toLowerCase().includes(normalizedInput) &&
        station !== origin &&
        station !== destination
      )
      .slice(0, 5); // Limit to 5 suggestions

    setStationSuggestions(suggestions);
    handleFilterChange('viaStation', value);
  };

  // Add function to handle station selection
  const handleStationSelect = (station) => {
    handleFilterChange('viaStation', station);
    setStationSuggestions([]);
  };

  // Add function to clear station filter
  const clearStationFilter = () => {
    handleFilterChange('viaStation', '');
    setStationSuggestions([]);
  };

  // Add these new components right before the return statement
  const FilterPanel = () => (
    <>
      <div className="filter-section">
        <TimeRangeSelector
          label="Vertrektijd"
          value={filters.departureTimeRange}
          onChange={(value) => handleFilterChange('departureTimeRange', value)}
        />
      </div>
      
      <div className="filter-section">
        <TimeRangeSelector
          label="Aankomsttijd"
          value={filters.arrivalTimeRange}
          onChange={(value) => handleFilterChange('arrivalTimeRange', value)}
        />
      </div>

      <div className="filter-section">
        <h4>Maximum aantal overstappen</h4>
        <div className="time-range-slider">
          <div className="range-label">
            {filters.maxTransfers} overstap{filters.maxTransfers !== 1 ? 'pen' : ''}
          </div>
          <div className="track-container">
            <div 
              className="track-overlay" 
              style={{
                left: '0%',
                width: `${(filters.maxTransfers / 9) * 100}%`
              }}
            />
            <input
              type="range"
              min="0"
              max="9"
              value={filters.maxTransfers}
              onChange={(e) => handleFilterChange('maxTransfers', parseInt(e.target.value))}
            />
          </div>
        </div>
      </div>

      <JourneyTypeSelector />
      <ComplexitySelector />
      <TrainTypesFilter />

      <div className="filter-section full-width">
        <h4>Reizen met vervoerders</h4>
        <div className="vendor-filters">
          {loading ? (
            <div className="operator-loading-text">Reisadviezen worden nog geladen...</div>
          ) : (
            [...uniqueOperators].map(operator => {
              const isWalking = operator === "N/A";
              return (
                <label key={operator} className="vendor-checkbox-label">
                  <input
                    type="checkbox"
                    checked={filters.operators[operator] || false}
                    onChange={() => handleFilterChange('operators', { 
                      ...filters.operators, 
                      [operator]: !filters.operators[operator] 
                    })}
                    className="vendor-checkbox"
                  />
                  <div className="vendor-logo-container filter">
                    {isWalking ? (
                      <div className="walking-icon">🚶</div>
                    ) : operator.startsWith("Bus") ? (
                      <div className="bus-icon">🚌</div>
                    ) : (
                      <img 
                        src={`${baseUrl}/icons/${getOperatorLogo(operator)}.png`} 
                        alt={operator} 
                        className="vendor-logo"
                      />
                    )}
                  </div>
                </label>
              );
            })
          )}
        </div>
      </div>

      <div className="filter-section full-width">
        <h4>Reizen via station</h4>
        <div className="via-station-container">
          <input
            type="text"
            value={filters.viaStation}
            onChange={(e) => handleStationInput(e.target.value)}
            placeholder="Voer een stationsnaam in"
            className="via-station-input"
          />
          {filters.viaStation && (
            <button 
              className="via-station-clear"
              onClick={clearStationFilter}
              title="Filter verwijderen"
            >
              ✕
            </button>
          )}
          {stationSuggestions.length > 0 && (
            <div className="station-suggestions">
              {stationSuggestions.map((station, index) => (
                <div
                  key={index}
                  className="station-suggestion"
                  onClick={() => handleStationSelect(station)}
                >
                  {station}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="filter-section full-width">
        <h4>Boeken via</h4>
        <div className="vendor-filters">
          {Object.entries(filters.vendors).map(([vendor, selected]) => {
            let vendorLogo = "";
            if (vendor === "NS") {
              vendorLogo = "nederlandsespoorwegen";
            } else if (vendor === "DB") {
              vendorLogo = "dbfernverkehrag";
            } else if (vendor === "European Sleeper") {
              vendorLogo = "europeansleeper";
            }

            return (
              <label key={vendor} className="vendor-checkbox-label">
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={() => handleFilterChange('vendors', { 
                    ...filters.vendors, 
                    [vendor]: !selected 
                  })}
                  className="vendor-checkbox"
                />
                <div className="vendor-logo-container filter">
                  <img 
                    src={`${baseUrl}/icons/${vendorLogo}.png`} 
                    alt={vendor} 
                    className="vendor-logo"
                  />
                </div>
              </label>
            );
          })}
        </div>

        <div className="filtered-trips-count">
          {(() => {
            const filteredCount = filterTrips(tripOptions).length;
            return (
              <>
                <span className="filtered-trips-number">{filteredCount}</span>
                <span>reisadvie{filteredCount === 1 ? 's' : 'zen'} {filteredCount === 1 ? 'voldoet' : 'voldoen'} aan deze filters</span>
              </>
            );
          })()}
        </div>

        <div className="filter-actions">
          <button onClick={handleResetFilters} className="reset-filters-button">
            Filters resetten
          </button>
          
          <button 
            onClick={() => {
              navigator.clipboard.writeText(window.location.href).then(() => {
                // Show a brief success message
                const button = document.activeElement;
                const originalText = button.textContent;
                button.textContent = 'Gekopieerd!';
                button.style.backgroundColor = '#4CAF50';
                setTimeout(() => {
                  button.textContent = originalText;
                  button.style.backgroundColor = '';
                }, 2000);
              }).catch(err => {
                console.error('Failed to copy URL:', err);
                // Fallback: show URL in alert
                alert(`Deel deze URL:\n${window.location.href}`);
              });
            }}
            className={`share-filters-button ${!areFiltersDefault(filters) ? 'active-filters' : ''}`}
            title={areFiltersDefault(filters) ? "Deel deze resultaten" : "Deel deze gefilterde resultaten"}
          >
            {areFiltersDefault(filters) ? '📤 Deel resultaten' : '📤 Deel filters'}
          </button>
        </div>
      </div>
    </>
  );

  const ComplexitySelector = () => (
    <div className="filter-section">
      <h4>Moeilijkheidsgraad</h4>
      <select 
        value={filters.complexity} 
        onChange={(e) => handleFilterChange('complexity', e.target.value)}
        className="complexity-selector"
      >
        <option value="3">Alle reizen</option>
        <option value="2">Tot iets moeilijkere reizen</option>
        <option value="1">Alleen geschikt voor beginners</option>
      </select>
    </div>
  );

  const TrainTypesFilter = () => {
    const trainTypeMap = {
      regional: "Stoptrein",
      national: "Intercity",
      regionalExpress: "Sneltrein",
      nationalExpress: "Hogesnelheidstrein",
      ferry: "Veerboot",
      suburban: "Stedelijk vervoer",
      "N/A": "Ander vervoer",
      "nighttrain": "Nachttrein ☾"
    };

    return (
      <div className="filter-section">
        <h4>Treintypen</h4>
        <div className="train-type-filters">
          {Object.entries(trainTypeMap).map(([key, label]) => (
            <label key={key} className="train-type-checkbox-label">
              <input
                type="checkbox"
                checked={filters.trainTypes[key] || false}
                onChange={() => handleFilterChange('trainTypes', { 
                  ...filters.trainTypes, 
                  [key]: !filters.trainTypes[key] 
                })}
                className="train-type-checkbox"
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
      </div>
    );
  };

  const JourneyTypeSelector = () => (
    <div className="filter-section">
      <h4>Dagtrein / nachttrein</h4>
      <select 
        value={filters.journeyType} 
        onChange={(e) => handleFilterChange('journeyType', e.target.value)}
        className="complexity-selector"
      >
        <option value="all">Alle reizen</option>
        <option value="day">Reizen overdag</option>
        <option value="night">Reizen 's nachts</option>
      </select>
    </div>
  );

  const handleResetSingleFilter = (filterKey) => {
    setFilters(prev => {
      const newFilters = { ...prev };
      switch (filterKey) {
        case 'journeyType':
          newFilters.journeyType = 'all';
          break;
        case 'departureTimeRange':
          newFilters.departureTimeRange = [0, 24];
          break;
        case 'arrivalTimeRange':
          newFilters.arrivalTimeRange = [0, 24];
          break;
        case 'maxTransfers':
          newFilters.maxTransfers = 9;
          break;
        case 'operators':
          newFilters.operators = Object.fromEntries(Object.keys(prev.operators).map(op => [op, true]));
          break;
        case 'vendors':
          newFilters.vendors = Object.fromEntries(Object.keys(prev.vendors).map(v => [v, true]));
          break;
        case 'viaStation':
          newFilters.viaStation = '';
          break;
        case 'complexity':
          newFilters.complexity = '3';
          break;
        case 'trainTypes':
          newFilters.trainTypes = Object.fromEntries(Object.keys(prev.trainTypes).map(t => [t, true]));
          break;
        default:
          break;
      }
      updateURLWithFilters(newFilters);
      return newFilters;
    });
  };

  const FilterLabels = () => (
    <>
      {filters.journeyType !== 'all' && (
        <span className="filter-tag">
          {filters.journeyType === 'day' ? 'Reizen overdag' : "Reizen 's nachts"}
          <button className="filter-tag-x" aria-label="Verwijder filter" onClick={() => handleResetSingleFilter('journeyType')}>×</button>
        </span>
      )}
      {filters.departureTimeRange[0] !== 0 || filters.departureTimeRange[1] !== 24 ? (
        <span className="filter-tag">Vertrek: {filters.departureTimeRange[0]}:00-{filters.departureTimeRange[1]}:00
          <button className="filter-tag-x" aria-label="Verwijder filter" onClick={() => handleResetSingleFilter('departureTimeRange')}>×</button>
        </span>
      ) : null}
      {filters.arrivalTimeRange[0] !== 0 || filters.arrivalTimeRange[1] !== 24 ? (
        <span className="filter-tag">Aankomst: {filters.arrivalTimeRange[0]}:00-{filters.arrivalTimeRange[1]}:00
          <button className="filter-tag-x" aria-label="Verwijder filter" onClick={() => handleResetSingleFilter('arrivalTimeRange')}>×</button>
        </span>
      ) : null}
      {filters.maxTransfers !== 9 ? (
        <span className="filter-tag">Max {filters.maxTransfers} overstap{filters.maxTransfers !== 1 ? 'pen' : ''}
          <button className="filter-tag-x" aria-label="Verwijder filter" onClick={() => handleResetSingleFilter('maxTransfers')}>×</button>
        </span>
      ) : null}
      {Object.entries(filters.operators).some(([_, selected]) => !selected) ? (
        <span className="filter-tag">Vervoerders gefilterd
          <button className="filter-tag-x" aria-label="Verwijder filter" onClick={() => handleResetSingleFilter('operators')}>×</button>
        </span>
      ) : null}
      {Object.entries(filters.vendors).some(([_, selected]) => !selected) ? (
        <span className="filter-tag">Verkopers gefilterd
          <button className="filter-tag-x" aria-label="Verwijder filter" onClick={() => handleResetSingleFilter('vendors')}>×</button>
        </span>
      ) : null}
      {filters.viaStation ? (
        <span className="filter-tag">Via: {filters.viaStation}
          <button className="filter-tag-x" aria-label="Verwijder filter" onClick={() => handleResetSingleFilter('viaStation')}>×</button>
        </span>
      ) : null}
      {filters.complexity !== '3' && (
        <span className="filter-tag">
          {filters.complexity === '1' ? 'Alleen geschikt voor beginners' : 'Tot iets moeilijkere reizen'}
          <button className="filter-tag-x" aria-label="Verwijder filter" onClick={() => handleResetSingleFilter('complexity')}>×</button>
        </span>
      )}
      {Object.values(filters.trainTypes).some(value => !value) && (
        <span className="filter-tag">Treintypen gefilterd
          <button className="filter-tag-x" aria-label="Verwijder filter" onClick={() => handleResetSingleFilter('trainTypes')}>×</button>
        </span>
      )}
    </>
  );

  // Helper function to determine if a station is Dutch
  const isDutchStation = (stationName) => {
    if (!stationName) return false;
    
    // Normalize the station name for comparison
    const normalizedName = stationName.toLowerCase()
      .replace(/[()]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    
    // Check if the station is in the Dutch stations list
    return nlStations.some(dutchStation => 
      dutchStation.toLowerCase() === normalizedName
    );
  };

  // Helper function to determine if a journey involves a Dutch station
  const hasDutchStation = (origin, destination) => {
    return isDutchStation(origin) || isDutchStation(destination);
  };

  // Helper function to get the Dutch and non-Dutch stations
  const getDutchAndNonDutchStations = (origin, destination) => {
    const isOriginDutch = isDutchStation(origin);
    const isDestinationDutch = isDutchStation(destination);
    
    if (isOriginDutch && !isDestinationDutch) {
      return { dutchStation: origin, nonDutchStation: destination, isReverse: false };
    } else if (!isOriginDutch && isDestinationDutch) {
      return { dutchStation: destination, nonDutchStation: origin, isReverse: true };
    } else if (isOriginDutch && isDestinationDutch) {
      // Both are Dutch - treat the first one as the "Dutch" station for route purposes
      return { dutchStation: origin, nonDutchStation: destination, isReverse: false };
    } else {
      // Neither is Dutch - return null to indicate no Interrail recommendation needed
      return null;
    }
  };

  const calculateInterrailWaypoints = (origin, destination, tripOptions) => {
    // Use the simple and reliable isTripInNL logic instead of complex station name matching
    const isTripInNL = nsOrigin.startsWith('NL') || nsDestination.startsWith('NL');
    
    if (!isTripInNL) {
      // Neither origin nor destination is Dutch, so no Interrail recommendation
      return [];
    }

    // Determine which station is Dutch and which is not
    const isOriginDutch = nsOrigin.startsWith('NL');
    const isDestinationDutch = nsDestination.startsWith('NL');
    
    let dutchStation, nonDutchStation, isReverse;
    
    if (isOriginDutch && !isDestinationDutch) {
      dutchStation = origin;
      nonDutchStation = destination;
      isReverse = false;
    } else if (!isOriginDutch && isDestinationDutch) {
      dutchStation = destination;
      nonDutchStation = origin;
      isReverse = true;
    } else if (isOriginDutch && isDestinationDutch) {
      // Both are Dutch - treat the first one as the "Dutch" station for route purposes
      dutchStation = origin;
      nonDutchStation = destination;
      isReverse = false;
    } else {
      // Neither is Dutch - return empty array (this shouldn't happen given the isTripInNL check above)
      return [];
    }
    
    // For route purposes, we'll use "Amsterdam" as the representative Dutch station
    // This allows us to reuse the existing scenic route logic
    const effectiveOrigin = 'Amsterdam'; // Always use Amsterdam as origin for scenic routes
    const effectiveDestination = nonDutchStation; // Always use the foreign city as destination



    // Define logical border crossing points and major hubs for different regions
    const borderCrossings = {
      // German-Polish border
      'frankfurt-oder': ['Frankfurt(Oder)', 'Frankfurt Oder', 'Frankfurt (Oder)'],
      'berlin': ['Berlin Hbf', 'Berlin Ostbahnhof', 'Berlin Ostkreuz', 'Berlin Zoologischer Garten'],
      
      // German-Swiss border
      'basel': ['Basel SBB', 'Basel Bad Bf', 'Basel'],
      
      // German-Austrian border
      'salzburg': ['Salzburg Hbf', 'Salzburg'],
      'kufstein': ['Kufstein'],
      
      // German-Czech border
      'dresden': ['Dresden Hbf', 'Dresden'],
      'cheb': ['Cheb'],
      
      // French-Swiss border
      'geneve': ['Genève', 'Geneva'],
      
      // French-Italian border
      'lyon': ['Lyon Part-Dieu', 'Lyon Perrache', 'Lyon'],
      
      // Major hubs
      'frankfurt': ['Frankfurt am Main', 'Frankfurt Hbf', 'Frankfurt'],
      'münchen': ['München Hbf', 'München', 'Munich'],
      'hamburg': ['Hamburg Hbf', 'Hamburg'],
      'kopenhagen': ['København H', 'Kopenhagen', 'Copenhagen'],
      'parijs': ['Paris Gare de Lyon', 'Paris Gare du Nord', 'Paris', 'Parijs']
    };

    // Define scenic route waypoints for specific origin-destination pairs
    const scenicRoutes = {
      // Amsterdam to Italy scenic routes - Bernina Express (one of the most scenic routes in the world)
      'amsterdam-milano': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Chur', overnight: true, description: 'Overnacht in Chur - toegangspoort tot de Alpen', slug: 'chur' },
            { station: 'St. Moritz', overnight: true, description: 'Overnacht in St. Moritz - luxe bergresort', slug: 'st-moritz' },
            { station: 'Tirano', overnight: false, description: 'Overstappunt voor Bernina Express', slug: 'tirano' }
          ],
          description: 'Via de Bernina Express',
          details: 'Reis door de Zwitserse Alpen via de beroemde Bernina Express - UNESCO Werelderfgoed'
        },
        {
          type: 'practical',
          waypoints: [
            { station: 'Basel', overnight: true, description: 'Overnacht in Basel - Zwitserse cultuurstad', slug: 'basel' }
          ],
          description: 'Via Basel en Zürich',
          details: 'Snelle en efficiënte route door Zwitserland'
        }
      ],
      'amsterdam-rome': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Chur', overnight: true, description: 'Overnacht in Chur - toegangspoort tot de Alpen', slug: 'chur' },
            { station: 'St. Moritz', overnight: true, description: 'Overnacht in St. Moritz - luxe bergresort', slug: 'st-moritz' },
            { station: 'Tirano', overnight: false, description: 'Overstappunt voor Bernina Express', slug: 'tirano' },
            { station: 'Florence', overnight: true, description: 'Overnacht in Florence - Italiaanse kunststad', slug: 'florence' }
          ],
          description: 'Via de Bernina Express en Toscane',
          details: 'Combineer berglandschap met Italiaanse kunststeden'
        },
        {
          type: 'scenic',
          waypoints: [
            { station: 'Venetië', overnight: true, description: 'Overnacht in Venetië - romantische kanaalstad', slug: 'venetie' },
            { station: 'Florence', overnight: true, description: 'Overnacht in Florence - Italiaanse kunststad', slug: 'florence' }
          ],
          description: 'Via Venetië en Toscane',
          details: 'Klassieke Italiaanse stedentrip route, het best te bereiken via de European Sleeper nachttrein naar Venetië (indien deze op dit moment rijdt)'
        },
        {
          type: 'practical',
          waypoints: [
            { station: 'Basel', overnight: true, description: 'Overnacht in Basel - Zwitserse cultuurstad', slug: 'basel' },
            { station: 'Milaan', overnight: true, description: 'Overnacht in Milaan - Italiaanse modehoofdstad', slug: 'milaan' },
            { station: 'Florence', overnight: false, description: 'Overstappunt voor hogesnelheidstreinen', slug: 'florence' }
          ],
          description: 'Via Basel, Milaan en Florence',
          details: 'Relatief snelle route door grote Alpentunnels'
        },
      ],
      // Amsterdam to Austria scenic routes - Arlberg Railway
      'amsterdam-vienna': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Innsbruck', overnight: true, description: 'Overnacht in Innsbruck - Tiroolse hoofdstad' },
            { station: 'St. Anton am Arlberg', overnight: true, description: 'Overnacht in St. Anton - Arlberg pas' }
          ],
          description: 'Via de Arlberg Railway',
          details: 'Reis door de Oostenrijkse Alpen via de spectaculaire Arlberg Railway'
        },
        {
          type: 'scenic',
          waypoints: [
            { station: 'Salzburg', overnight: true, description: 'Overnacht in Salzburg - Mozart\'s geboorteplaats' },
            { station: 'Innsbruck', overnight: true, description: 'Overnacht in Innsbruck - Tiroolse hoofdstad' }
          ],
          description: 'Via Salzburg en de Alpen',
          details: 'Combineer cultuur met berglandschap via de Oostenrijkse Alpen'
        },
        {
          type: 'practical',
          waypoints: [
            { station: 'München', overnight: true, description: 'Overnacht in München - Beierse hoofdstad' },
            { station: 'Salzburg', overnight: false, description: 'Overstappunt voor Oostenrijkse treinen' }
          ],
          description: 'Via München en Salzburg',
          details: 'Snelle route door Duitsland naar Oostenrijk'
        }
      ],
      // Amsterdam to Switzerland scenic routes - More logical routes
      'amsterdam-zurich': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Offenburg', overnight: true, description: 'Overnacht in Offenburg - toegangspoort tot het Zwarte Woud' },
            { station: 'Schaffhausen', overnight: true, description: 'Overnacht in Schaffhausen - Zwitserse stad aan de Rijn' }
          ],
          description: 'Via het Zwarte Woud',
          details: 'Reis door het mysterieuze Zwarte Woud van Offenburg naar Zwitserland'
        },
        {
          type: 'scenic',
          waypoints: [
            { station: 'Basel', overnight: true, description: 'Overnacht in Basel - Zwitserse cultuurstad' },
            { station: 'Luzern', overnight: true, description: 'Overnacht in Luzern - meer- en bergzicht' }
          ],
          description: 'Via Basel en Luzern',
          details: 'Reis door het Zwitserse berglandschap via Luzern'
        },
        {
          type: 'practical',
          waypoints: [
            { station: 'Basel', overnight: true, description: 'Overnacht in Basel - Zwitserse cultuurstad' }
          ],
          description: 'Via Basel',
          details: 'Snelle en efficiënte route door Zwitserland'
        }
      ],
      // Amsterdam to France scenic routes - Côte d'Azur and Provence
      'amsterdam-paris': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Brussel', overnight: true, description: 'Overnacht in Brussel - Belgische en Europese hoofdstad' },
            { station: 'Lille', overnight: false, description: 'Overstappunt in Noord-Frankrijk' }
          ],
          description: 'Via België en Noord-Frankrijk',
          details: 'Ontdek Vlaamse en Franse cultuur'
        },
        {
          type: 'practical',
          waypoints: [
            { station: 'Brussel', overnight: true, description: 'Overnacht in Brussel - Belgische en Europese hoofdstad' },
            { station: 'Parijs', overnight: false, description: 'Directe verbinding naar Parijs' }
          ],
          description: 'Via Brussel naar Parijs',
          details: 'Snelle en directe route naar de lichtstad'
        }
      ],
      // Amsterdam to Spain scenic routes - Côte d'Azur and Mediterranean
      'amsterdam-barcelona': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Toulouse', overnight: true, description: 'Overnacht in Toulouse - roze stad van Occitanië' },
            { station: 'Latour de Carol', overnight: true, description: 'Overnacht in Latour de Carol - bergstation in de Pyreneeën' }
          ],
          description: 'Via de Pyreneeën',
          details: 'Reis door de Franse Pyreneeën via het bergstation Latour de Carol'
        },
        {
          type: 'practical',
          waypoints: [
            { station: 'Parijs', overnight: true, description: 'Overnacht in Parijs - lichtstad en cultuurhoofdstad' }
          ],
          description: 'Via Parijs',
          details: 'Directe route via de Franse hoofdstad'
        }
      ],
      // Amsterdam to Germany scenic routes - Rhine Valley and Black Forest
      'amsterdam-berlin': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Keulen', overnight: true, description: 'Overnacht in Keulen - kathedraalstad aan de Rijn' },
            { station: 'Mainz', overnight: true, description: 'Overnacht in Mainz - Rijnvallei' }
          ],
          description: 'Via de Rijnvallei',
          details: 'Reis door de spectaculaire Rijnvallei met kastelen en wijngaarden'
        },
        {
          type: 'scenic',
          waypoints: [
            { station: 'Freiburg', overnight: true, description: 'Overnacht in Freiburg - toegangspoort tot het Zwarte Woud' },
            { station: 'Stuttgart', overnight: true, description: 'Overnacht in Stuttgart - hoofdstad van Baden-Württemberg' }
          ],
          description: 'Via het Zwarte Woud',
          details: 'Reis door het mysterieuze Zwarte Woud en Zuid-Duitsland'
        }
      ],
      // Amsterdam to Scandinavia scenic routes - Coastal routes
      'amsterdam-stockholm': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Kopenhagen', overnight: true, description: 'Overnacht in Kopenhagen - Deense hoofdstad' },
            { station: 'Gothenburg', overnight: true, description: 'Overnacht in Gothenburg - Zweedse westkust' }
          ],
          description: 'Via Scandinavische kust',
          details: 'Reis door Scandinavische kustgebieden en over de Øresundbrug'
        }
      ],
      'amsterdam-oslo': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Kopenhagen', overnight: true, description: 'Overnacht in Kopenhagen - Deense hoofdstad' },
            { station: 'Gothenburg', overnight: true, description: 'Overnacht in Gothenburg - Zweedse westkust' }
          ],
          description: 'Via Scandinavische kust',
          details: 'Scandinavische kustroute naar Noorwegen via de Øresundbrug'
        }
      ],
      // Amsterdam to Scotland scenic routes - Settle-Carlisle Railway
      'amsterdam-edinburgh': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Londen', overnight: true, description: 'Overnacht in Londen - Britse hoofdstad' },
            { station: 'Leeds', overnight: true, description: 'Overnacht in Leeds - Yorkshire stad' },
            { station: 'Carlisle', overnight: true, description: 'Overnacht in Carlisle - historische grensstad' }
          ],
          description: 'Via de Settle-Carlisle Railway',
          details: 'Reis door de Yorkshire Dales en Pennines via de beroemde Settle-Carlisle spoorlijn'
        }
      ],
      'amsterdam-glasgow': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Londen', overnight: true, description: 'Overnacht in Londen - Britse hoofdstad' },
            { station: 'Leeds', overnight: true, description: 'Overnacht in Leeds - Yorkshire stad' },
            { station: 'Carlisle', overnight: true, description: 'Overnacht in Carlisle - historische grensstad' }
          ],
          description: 'Via de Settle-Carlisle Railway',
          details: 'Reis door de Yorkshire Dales en Pennines via de beroemde Settle-Carlisle spoorlijn'
        }
      ],
      // Amsterdam to Eastern Europe scenic routes - Danube Valley and Bohemia
      'amsterdam-prague': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Dresden', overnight: true, description: 'Overnacht in Dresden - barokstad aan de Elbe' },
            { station: 'Karlovy Vary', overnight: true, description: 'Overnacht in Karlovy Vary - kuuroord in Bohemen' }
          ],
          description: 'Via Saksen en Bohemen',
          details: 'Reis door historisch Duitsland naar Tsjechië via de Elbevallei'
        }
      ],
      'amsterdam-budapest': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Wenen', overnight: true, description: 'Overnacht in Wenen - keizerlijke hoofdstad' },
            { station: 'Bratislava', overnight: true, description: 'Overnacht in Bratislava - Slowaakse hoofdstad' }
          ],
          description: 'Via de Donauvallei',
          details: 'Reis door de Donauvallei - het hart van Centraal-Europa'
        }
      ],
      // Additional scenic routes for other destinations
      'amsterdam-munich': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Stuttgart', overnight: true, description: 'Overnacht in Stuttgart - hoofdstad van Baden-Württemberg' },
            { station: 'Augsburg', overnight: true, description: 'Overnacht in Augsburg - historische stad in Beieren' }
          ],
          description: 'Via Zuid-Duitsland',
          details: 'Reis door het pittoreske Zuid-Duitsland naar Beieren'
        }
      ],
      'amsterdam-geneva': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Lausanne', overnight: true, description: 'Overnacht in Lausanne - stad aan het Meer van Genève' },
            { station: 'Montreux', overnight: true, description: 'Overnacht in Montreux - Zwitserse Rivièra' }
          ],
          description: 'Via het Meer van Genève',
          details: 'Reis langs de Zwitserse Rivièra en het Meer van Genève'
        }
      ],
      'amsterdam-lyon': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Dijon', overnight: true, description: 'Overnacht in Dijon - hoofdstad van Bourgondië' },
            { station: 'Beaune', overnight: true, description: 'Overnacht in Beaune - wijnhoofdstad van Bourgondië' }
          ],
          description: 'Via Bourgondië',
          details: 'Reis door het wijnland Bourgondië en de Franse provincie'
        }
      ],
      // Amsterdam to Nice scenic route - Côte d'Azur
      'amsterdam-nice': [
        {
          type: 'scenic',
          waypoints: [
            { station: 'Brussel', overnight: false, description: 'Overstappunt in Brussel - Belgische en Europese hoofdstad', slug: 'brussel' },
            { station: 'Marseille', overnight: true, description: 'Overnacht in Marseille - mediterrane havenstad', slug: 'marseille' }
          ],
          description: 'Via Brussel en Marseille',
          details: 'Reis langs de mediterrane kust naar de Côte d\'Azur'
        }
      ]
    };

    // Helper function to normalize station names for comparison
    const normalizeStation = (station) => {
      return station.toLowerCase()
        .replace(/[()]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    };

    // Helper function to find border crossing category for a station
    const findBorderCrossing = (station) => {
      const normalized = normalizeStation(station);
      for (const [category, stations] of Object.entries(borderCrossings)) {
        if (stations.some(s => normalizeStation(s).includes(normalized) || normalized.includes(normalizeStation(s)))) {
          return category;
        }
      }
      return null;
    };

    // Analyze transfer stations from actual trip data
    const transferStations = new Map(); // station -> { count, totalTransferTime, avgTransferTime, borderCrossing }
    const expensiveOperators = ['Eurostar', 'SNCF', 'European Sleeper', 'Urlaubs-Express'];
    
    tripOptions.forEach(trip => {
      // Penalize trips with expensive operators
      const hasExpensiveOperator = trip.legs.some(leg => 
        expensiveOperators.some(op => leg.operator.includes(op))
      );
      
      // Analyze each transfer in the trip
      for (let i = 1; i < trip.legs.length; i++) {
        const transferStation = trip.legs[i].origin;
        const transferTime = (new Date(trip.legs[i].departure) - new Date(trip.legs[i-1].arrival)) / (1000 * 60); // in minutes
        
        // Skip very short transfers (less than 10 minutes) as they're not good waypoints
        if (transferTime < 10) continue;
        
        const current = transferStations.get(transferStation) || { 
          count: 0, 
          totalTransferTime: 0, 
          avgTransferTime: 0,
          borderCrossing: findBorderCrossing(transferStation)
        };
        const weight = hasExpensiveOperator ? 0.5 : 1;
        
        transferStations.set(transferStation, {
          count: current.count + weight,
          totalTransferTime: current.totalTransferTime + transferTime,
          avgTransferTime: (current.totalTransferTime + transferTime) / (current.count + weight),
          borderCrossing: current.borderCrossing
        });
      }
    });

    // Filter and sort transfer stations, prioritizing border crossings
    const validTransferStations = Array.from(transferStations.entries())
      .filter(([station, data]) => {
        // Only include stations that appear in multiple trips and have reasonable transfer times
        return data.count >= 1 && data.avgTransferTime >= 15; // At least 15 minutes average transfer
      })
      .sort((a, b) => {
        // Prioritize border crossings first
        if (a[1].borderCrossing && !b[1].borderCrossing) return -1;
        if (!a[1].borderCrossing && b[1].borderCrossing) return 1;
        
        // Then sort by frequency and transfer time
        if (b[1].count !== a[1].count) {
          return b[1].count - a[1].count;
        }
        return b[1].avgTransferTime - a[1].avgTransferTime;
      })
      .map(([station, data]) => ({
        station,
        count: data.count,
        avgTransferTime: data.avgTransferTime,
        borderCrossing: data.borderCrossing
      }));

    // Helper function to estimate travel time between two stations using actual trip data
    const estimateTravelTime = (station1, station2) => {
      // Find trips that go between these stations or have them as transfer points
      let totalTime = 0;
      let count = 0;
      
      tripOptions.forEach(trip => {
        const stations = trip.legs.map(leg => leg.origin).concat(trip.legs[trip.legs.length - 1].destination);
        const station1Index = stations.findIndex(s => s.toLowerCase().includes(station1.toLowerCase()));
        const station2Index = stations.findIndex(s => s.toLowerCase().includes(station2.toLowerCase()));
        
        if (station1Index !== -1 && station2Index !== -1 && station1Index < station2Index) {
          // Calculate time between these stations
          const startTime = new Date(trip.legs[station1Index].departure);
          const endTime = new Date(trip.legs[station2Index].arrival);
          const travelTime = (endTime - startTime) / (1000 * 60); // in minutes
          
          if (travelTime > 0 && travelTime < 24 * 60) { // Reasonable travel time (less than 24 hours)
            totalTime += travelTime;
            count++;
          }
        }
      });
      
      // If we found actual data, return the average; otherwise use a reasonable fallback
      if (count > 0) {
        return (totalTime / count) * 60 * 1000; // Convert back to milliseconds
      }
      
      // Fallback: estimate based on typical European train speeds (6 hours)
      return 6 * 60 * 60 * 1000; // Default 6 hours in milliseconds
    };

    // Helper function to get scenic routes for a specific origin-destination pair
    const getScenicRoutes = (origin, destination) => {
      const originLower = origin.toLowerCase();
      const destLower = destination.toLowerCase();
      
      // Check for specific scenic route combinations
      // Since we're using "Amsterdam" as the effective station name for Dutch stations,
      // we can simply check for "amsterdam"
      if (originLower === 'amsterdam') {
        if (destLower.includes('milaan') || destLower.includes('milan')) {
          return scenicRoutes['amsterdam-milano'] || [];
        } else if (destLower.includes('rome') || destLower.includes('roma')) {
          return scenicRoutes['amsterdam-rome'] || [];
        } else if (destLower.includes('wenen') || destLower.includes('vienna')) {
          return scenicRoutes['amsterdam-vienna'] || [];
        } else if (destLower.includes('zürich') || destLower.includes('zurich') || 
                   destLower.includes('basel') || destLower.includes('bern') || 
                   destLower.includes('luzern') || destLower.includes('lausanne') ||
                   destLower.includes('interlaken') || destLower.includes('zermatt') ||
                   destLower.includes('st. moritz') || destLower.includes('geneva') ||
                   destLower.includes('switzerland') || destLower.includes('zwitserland')) {
          return scenicRoutes['amsterdam-zurich'] || [];
        } else if (destLower.includes('paris') || destLower.includes('france')) {
          return scenicRoutes['amsterdam-paris'] || [];
        } else if (destLower.includes('barcelona') || destLower.includes('madrid') || destLower.includes('spain')) {
          return scenicRoutes['amsterdam-barcelona'] || [];
        } else if (destLower.includes('berlin') || destLower.includes('germany')) {
          return scenicRoutes['amsterdam-berlin'] || [];
        } else if (destLower.includes('stockholm') || destLower.includes('sweden')) {
          return scenicRoutes['amsterdam-stockholm'] || [];
        } else if (destLower.includes('oslo') || destLower.includes('norway')) {
          return scenicRoutes['amsterdam-oslo'] || [];
        } else if (destLower.includes('prague') || destLower.includes('czech')) {
          return scenicRoutes['amsterdam-prague'] || [];
        } else if (destLower.includes('budapest') || destLower.includes('hungary')) {
          return scenicRoutes['amsterdam-budapest'] || [];
        } else if (destLower.includes('münchen') || destLower.includes('munich')) {
          return scenicRoutes['amsterdam-munich'] || [];
        } else if (destLower.includes('geneve') || destLower.includes('geneva')) {
          return scenicRoutes['amsterdam-geneva'] || [];
        } else if (destLower.includes('lyon')) {
          return scenicRoutes['amsterdam-lyon'] || [];
        } else if (destLower.includes('nice')) {
          return scenicRoutes['amsterdam-nice'] || [];
        } else if (destLower.includes('edinburgh') || destLower.includes('scotland')) {
          return scenicRoutes['amsterdam-edinburgh'] || [];
        } else if (destLower.includes('glasgow')) {
          return scenicRoutes['amsterdam-glasgow'] || [];
        }
      }
      
      // No reverse route logic needed here since we handle it in calculateInterrailWaypoints
      return [];
    };

    // Create route options based on geographic logic
    const routeOptions = [];
    
    // Create the main geographic route (practical route)
    const mainWaypoints = createGeographicRoute(effectiveOrigin, effectiveDestination, validTransferStations);
    if (mainWaypoints.length > 0) {
      // Convert string waypoints to object format for practical routes
      const practicalWaypoints = mainWaypoints.map(station => ({
        station: station,
        overnight: false, // Practical routes typically don't include overnight stays
        description: 'Overstappunt'
      }));
      
      // Only add practical routes if they have overnight stays (which they typically don't)
      // This ensures Interrail recommendations only show when they make economic sense
      const hasOvernightStays = practicalWaypoints.some(wp => wp.overnight);
      if (hasOvernightStays) {
        routeOptions.push({
          type: 'practical',
          waypoints: practicalWaypoints,
          description: 'Via logische overstapstations',
          details: 'Snelle en efficiënte route'
        });
      }
    }

    // Add scenic routes if available (these typically have overnight stays)
    const scenicRouteOptions = getScenicRoutes(effectiveOrigin, effectiveDestination);
    
    // If this is a reverse journey, we need to reverse the waypoints and adjust descriptions
    if (isReverse && scenicRouteOptions.length > 0) {
      const reversedScenicRoutes = scenicRouteOptions.map(route => ({
        ...route,
        waypoints: [...route.waypoints].reverse().map(waypoint => ({
          ...waypoint,
          description: waypoint.description
        })),
        description: route.description,
        details: route.details
      }));
      routeOptions.push(...reversedScenicRoutes);
    } else {
      routeOptions.push(...scenicRouteOptions);
    }

    // If no routes found, create a fallback
    if (routeOptions.length === 0) {
      const fallbackRoutes = createIntelligentFallbackRoutes(origin, destination, validTransferStations, tripOptions);
      routeOptions.push(...fallbackRoutes);
    }

    // Filter out routes that don't have any overnight stays
    const routesWithOvernightStays = routeOptions.filter(route => 
      route.waypoints.some(waypoint => waypoint.overnight)
    );

    // If we have routes with overnight stays, return those; otherwise return all routes
    return routesWithOvernightStays.length > 0 ? routesWithOvernightStays : routeOptions;
  };

  // Helper function to create intelligent fallback routes with proper day splitting
  const createIntelligentFallbackRoutes = (origin, destination, transferStations, tripOptions) => {
    if (transferStations.length === 0) return [];

    // Calculate total trip duration from actual trip data
    const totalTripDuration = calculateAverageTripDuration(tripOptions);
    const totalHours = totalTripDuration / (1000 * 60 * 60); // Convert to hours

    // Determine optimal number of days based on duration
    let optimalDays = 1;
    if (totalHours > 6 && totalHours <= 12) {
      optimalDays = 2;
    } else if (totalHours > 12 && totalHours <= 18) {
      optimalDays = 3;
    } else if (totalHours > 18 && totalHours <= 24) {
      optimalDays = 4;
    } else if (totalHours > 24) {
      optimalDays = Math.ceil(totalHours / 6); // One day per 6 hours for very long trips
    }



    // Create routes with different strategies
    const routes = [];

    // Strategy 1: Geographic route with overnight stays
    if (optimalDays > 1) {
      // Use the actual origin and destination for geographic routing
      const geographicWaypoints = createGeographicRoute(origin, destination, transferStations);
      if (geographicWaypoints.length > 0) {
        const waypointsWithOvernight = geographicWaypoints.map((station, index) => ({
          station: station,
          overnight: index < optimalDays - 1, // All except the last waypoint get overnight stays
          description: index < optimalDays - 1 ? 'Overnacht - logische tussenstop' : 'Overstappunt'
        }));

        const actualTravelDays = calculateInterrailDays(waypointsWithOvernight);
        routes.push({
          type: 'practical',
          waypoints: waypointsWithOvernight,
          description: `Via ${geographicWaypoints.length} tussenstops`,
          details: `${actualTravelDays} dagen reis - ${Math.round(totalHours / actualTravelDays)} uur per dag`,
          actualTransfers: geographicWaypoints.length
        });
      }
    }

    // Strategy 2: Frequency-based route (most common transfer stations)
    // Filter out stations that are too close to each other (same city)
    const uniqueStations = [];
    const seenCities = new Set();
    
    for (const station of transferStations) {
      const stationName = station.station.toLowerCase();
      const isDuplicate = uniqueStations.some(existing => {
        const existingName = existing.station.toLowerCase();
        // Check if stations are in the same city (Berlin Hbf vs Berlin Ostbahnhof, etc.)
        return stationName.includes(existingName.split(' ')[0]) || existingName.includes(stationName.split(' ')[0]);
      });
      
      if (!isDuplicate) {
        uniqueStations.push(station);
      }
    }

    const frequencyWaypoints = uniqueStations
      .slice(0, 1) // Only 1 waypoint to prevent backtracking
      .map((station, index) => ({
        station: station.station,
        overnight: index < 1, // Only the first waypoint gets overnight stay
        description: index < 1 ? 'Overnacht - veel voorkomende overstap' : 'Overstappunt'
      }));

    if (frequencyWaypoints.length > 0) {
      const actualTravelDays = calculateInterrailDays(frequencyWaypoints);
      routes.push({
        type: 'fallback',
        waypoints: frequencyWaypoints,
        description: 'Via meest voorkomende overstapstations',
        details: `${actualTravelDays} dagen reis - gebaseerd op reële reisdata`,
        actualTransfers: frequencyWaypoints.length
      });
    }

    // Strategy 3: Border crossing route (if we have border crossings)
    const borderCrossingStations = transferStations.filter(s => s.borderCrossing);
    if (borderCrossingStations.length > 0) {
      // Use the same geographic logic as createGeographicRoute to prevent backtracking
      const geographicBorderWaypoints = createGeographicRoute(origin, destination, borderCrossingStations);
      
      if (geographicBorderWaypoints.length > 0) {
        const borderWaypoints = geographicBorderWaypoints
          .slice(0, Math.min(optimalDays - 1, 2)) // Max 2 border crossings to prevent backtracking
          .map((station, index) => ({
            station: station,
            overnight: index < optimalDays - 1,
            description: index < optimalDays - 1 ? 'Overnacht - grensoverschrijding' : 'Overstappunt'
          }));

        if (borderWaypoints.length > 0) {
          routes.push({
            type: 'practical',
            waypoints: borderWaypoints,
            description: 'Via grensoverschrijdingen',
            details: `${optimalDays} dagen reis - logische grensstops`,
            actualTransfers: borderWaypoints.length
          });
        }
      }
    }

    // Deduplicate routes - remove routes that are too similar
    const uniqueRoutes = [];
    for (const route of routes) {
      const routeKey = route.waypoints.map(wp => wp.station).join('→');
      const isDuplicate = uniqueRoutes.some(existing => {
        const existingKey = existing.waypoints.map(wp => wp.station).join('→');
        return routeKey === existingKey;
      });
      
      if (!isDuplicate) {
        uniqueRoutes.push(route);
      }
    }



    return uniqueRoutes;
  };

  // Helper function to calculate average trip duration
  const calculateAverageTripDuration = (tripOptions) => {
    if (tripOptions.length === 0) return 0;
    
    const totalDuration = tripOptions.reduce((sum, trip) => {
      return sum + (new Date(trip.arrival) - new Date(trip.departure));
    }, 0);
    
    return totalDuration / tripOptions.length;
  };

  // Calculate number of Interrail days needed
  const calculateInterrailDays = (waypoints) => {
    // Count overnight stays to determine Interrail days needed
    const overnightStays = waypoints.filter(wp => wp.overnight).length;
    
    // Each overnight stay counts as one day, plus one day for the final destination
    return overnightStays + 1;
  };

  // Interrail recommendation component
  const InterrailRecommendation = ({ origin, destination, routeOptions, interrailDays, isLongTrip = true }) => {
    // Function to analyze route-specific costs and provide targeted warnings
    const analyzeRouteCosts = (route, origin, destination) => {
      const warnings = [];
      const waypoints = [origin, ...route.waypoints.map(wp => wp.station), destination];
      
      // Check for France route (TGV reservations required)
      const hasFranceRoute = waypoints.some(wp => 
        wp.toLowerCase().includes('paris') || 
        wp.toLowerCase().includes('lyon') || 
        wp.toLowerCase().includes('marseille') ||
        wp.toLowerCase().includes('nice') ||
        wp.toLowerCase().includes('valence')
      );
      
      if (hasFranceRoute) {
        // Check if it's specifically Amsterdam-Paris (Eurostar)
        const isAmsterdamParis = origin.toLowerCase().includes('amsterdam') && waypoints.some(wp => wp.toLowerCase().includes('paris'));
        
        if (isAmsterdamParis) {
          warnings.push({
            type: 'eurostar-amsterdam',
            message: '⚠️ Amsterdam-Parijs wordt geëxploiteerd door Eurostar en vereist verplichte reserveringen (€32-37 per rit, afhankelijk van klasse). Controleer of losse tickets voor jouw reisdata goedkoper zijn dan Interrail.',
            icon: '🚄'
          });
          
          // Also add TGV warning if destination is beyond Paris (like Barcelona)
          const hasDestinationBeyondParis = destination.toLowerCase().includes('barcelona') || 
                                           destination.toLowerCase().includes('madrid') ||
                                           destination.toLowerCase().includes('lyon') ||
                                           destination.toLowerCase().includes('marseille') ||
                                           destination.toLowerCase().includes('nice');
          
          if (hasDestinationBeyondParis) {
            warnings.push({
              type: 'tgv-beyond-paris',
              message: '⚠️ TGV treinen in Frankrijk vereisen verplichte reserveringen (€17-45 per rit, afhankelijk van route en klasse). Controleer of losse tickets voor jouw reisdata goedkoper zijn dan Interrail.',
              icon: '🚄'
            });
          }
        } else {
          warnings.push({
            type: 'tgv',
            message: '⚠️ TGV treinen in Frankrijk vereisen verplichte zitplaatsreserveringen (€17-45 per rit, afhankelijk van route en klasse). Kosten worden per treinrit berekend. Controleer of losse tickets voor jouw reisdata goedkoper zijn dan Interrail.',
            icon: '🚄'
          });
        }
      }
      
      // Check for UK route (Eurostar reservations required)
      const hasUKRoute = waypoints.some(wp => 
        wp.toLowerCase().includes('london') || 
        wp.toLowerCase().includes('manchester') ||
        wp.toLowerCase().includes('edinburgh')
      );
      
      if (hasUKRoute) {
        warnings.push({
          type: 'eurostar-uk',
          message: '⚠️ Eurostar naar het VK vereist verplichte reserveringen (€35-40 per rit, afhankelijk van klasse). Dit is vaak duurder dan losse tickets.',
          icon: '🇬🇧'
        });
      }
      
      // Check for night train route
      const hasNightTrain = waypoints.some(wp => 
        wp.toLowerCase().includes('vienna') || 
        wp.toLowerCase().includes('budapest') ||
        wp.toLowerCase().includes('prague') ||
        wp.toLowerCase().includes('warsaw')
      );
      
      if (hasNightTrain) {
        warnings.push({
          type: 'nighttrain',
          message: '⚠️ Nachttreinen naar Oost-Europa vereisen vaak slaapwagenreserveringen (€20-80 per nacht).',
          icon: '🌙'
        });
      }
      
      // Check for Switzerland route (expensive country)
      const hasSwitzerlandRoute = waypoints.some(wp => 
        wp.toLowerCase().includes('basel') || 
        wp.toLowerCase().includes('zurich') ||
        wp.toLowerCase().includes('geneva') ||
        wp.toLowerCase().includes('lausanne') ||
        wp.toLowerCase().includes('bern') ||
        wp.toLowerCase().includes('chur') ||
        wp.toLowerCase().includes('st. moritz') ||
        wp.toLowerCase().includes('zermatt') ||
        wp.toLowerCase().includes('interlaken') ||
        wp.toLowerCase().includes('luzern')
      );
      
      if (hasSwitzerlandRoute) {
        warnings.push({
          type: 'switzerland',
          message: '⚠️ Zwitserland is een duur land, waardoor overnachtingen in Zwitserland vaak duurder zijn dan in andere landen.',
          icon: '🇨🇭'
        });
        
        // Add specific warning for TGV Lyria if route includes Paris
        if (waypoints.some(wp => wp.toLowerCase().includes('paris'))) {
          warnings.push({
            type: 'tgv-lyria',
            message: '⚠️ TGV Lyria naar Parijs vereist verplichte reserveringen (€25-35 per rit, afhankelijk van klasse).',
            icon: '🚄'
          });
        }
      }
      
      // Check for Italy route (high-speed trains require reservations)
      const hasItalyRoute = waypoints.some(wp => 
        wp.toLowerCase().includes('milan') || 
        wp.toLowerCase().includes('rome') ||
        wp.toLowerCase().includes('venice') ||
        wp.toLowerCase().includes('florence') ||
        wp.toLowerCase().includes('bologna')
      );
      
      if (hasItalyRoute) {
        warnings.push({
          type: 'italy',
          message: '⚠️ Italiaanse hogesnelheidstreinen (Frecciarossa) vereisen reserveringen (€11-13 per rit, afhankelijk van klasse).',
          icon: '🇮🇹'
        });
      }
      
      // Check for Germany route (ICE trains are generally good for Interrail)
      const hasGermanyRoute = waypoints.some(wp => 
        wp.toLowerCase().includes('berlin') || 
        wp.toLowerCase().includes('munich') ||
        wp.toLowerCase().includes('frankfurt') ||
        wp.toLowerCase().includes('hamburg') ||
        wp.toLowerCase().includes('cologne')
      );
      
      if (hasGermanyRoute) {
        warnings.push({
          type: 'germany',
          message: '✅ Duitse ICE treinen zijn uitstekend voor Interrail. Reserveringen zijn niet verplicht, wel aanbevolen en betaalbaar.',
          icon: '🇩🇪'
        });
      }
      
      // Check for Austria route (good for Interrail)
      const hasAustriaRoute = waypoints.some(wp => 
        wp.toLowerCase().includes('vienna') || 
        wp.toLowerCase().includes('salzburg') ||
        wp.toLowerCase().includes('innsbruck') ||
        wp.toLowerCase().includes('graz')
      );
      
      if (hasAustriaRoute) {
        warnings.push({
          type: 'austria',
          message: '✅ Oostenrijkse treinen zijn goed voor Interrail - geen reserveringen vereist.',
          icon: '🇦🇹'
        });
      }
      
      // Check for Poland route (night trains may require reservations)
      const hasPolandRoute = waypoints.some(wp => 
        wp.toLowerCase().includes('warsaw') || 
        wp.toLowerCase().includes('krakow') ||
        wp.toLowerCase().includes('katowice') ||
        wp.toLowerCase().includes('gdansk')
      );
      
      if (hasPolandRoute) {
        warnings.push({
          type: 'poland',
          message: '⚠️ Poolse nachttreinen vereisen vaak reserveringen (€10-30 per nacht).',
          icon: '🇵🇱'
        });
      }
      
      return warnings;
    };
    
    // Helper function to get route type styling
    const getRouteTypeStyle = (type) => {
      switch (type) {
        case 'practical':
          return 'route-practical';
        case 'scenic':
          return 'route-scenic';
        case 'fallback':
          return 'route-fallback';
        default:
          return 'route-default';
      }
    };

    // Helper function to get route type label
    const getRouteTypeLabel = (type) => {
      switch (type) {
        case 'practical':
          return '🚄 Praktisch';
        case 'scenic':
          return '🏔️ Mooie route';
        case 'fallback':
          return '📍 Standaard';
        default:
          return '📍 Route';
      }
    };
    
    return (
      <div className="trip-option interrail-recommendation">
        <div className="interrail-label">Interrail</div>
        <div className="trip-option-content">
          <div className="interrail-header">
            <div className="interrail-icon">🚂</div>
            <div className="interrail-text">
              <h3>
                {isLongTrip 
                  ? "Deze reis zou langer dan 10 uur duren"
                  : "Mooie routes beschikbaar"
                }
              </h3>
              <p>
                {isLongTrip 
                  ? "Wij raden je aan om deze reis in delen te splitsen door gebruik te maken van een Interrail pas"
                  : "Wij raden je aan om een Interrail pas te overwegen voor meer flexibiliteit en toegang tot mooie routes"
                }
              </p>
            </div>
          </div>
          
          <div className="interrail-main-content">
            <div className="interrail-route">
              <h4>Voorgestelde routes via Interrail:</h4>
              {routeOptions.map((route, routeIndex) => {
                const segments = [origin, ...route.waypoints, destination];
                const routeWarnings = analyzeRouteCosts(route, origin, destination);
                
                return (
                  <div key={routeIndex} className={`route-option ${getRouteTypeStyle(route.type)}`}>
                    <div className="route-header">
                      <span className="route-type-label">{getRouteTypeLabel(route.type)}</span>
                      <div className="route-description">{route.description}</div>
                    </div>
                    {route.details && (
                      <div className="route-details">{route.details}</div>
                    )}
                    <div className="waypoint-list">
                      {segments.map((segment, index) => {
                        // Handle both old string format and new object format
                        const isWaypoint = index > 0 && index < segments.length - 1;
                        const waypointData = isWaypoint && typeof route.waypoints[index - 1] === 'object' 
                          ? route.waypoints[index - 1] 
                          : { station: segment, overnight: false, description: '' };
                        
                        return (
                          <div key={index} className="waypoint-item">
                            {index > 0 && <div className="waypoint-arrow">→</div>}
                            <div className="waypoint-content">
                              {index === 0 || index === segments.length - 1 ? (
                                <span className="waypoint-station">{segment}</span>
                              ) : (
                                <div className="waypoint-with-info">
                                  <div className="waypoint-header">
                                    <a 
                                      href={`/bestemming/${waypointData.slug || slugify(waypointData.station)}`} 
                                      className="waypoint-link"
                                      target="_blank"
                                      rel="noopener noreferrer"
                                    >
                                      {waypointData.station}
                                    </a>
                                    {waypointData.overnight && (
                                      <span className="overnight-indicator" title={waypointData.description}>
                                        🌙
                                      </span>
                                    )}
                                  </div>
                                  {waypointData.description && (
                                    <div className="waypoint-description">
                                      {waypointData.description}
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Route-specific warnings */}
                    {routeWarnings.length > 0 && (
                      <div className={`route-warnings ${routeWarnings.every(w => w.type === 'germany' || w.type === 'austria') ? 'positive' : ''}`}>
                        {routeWarnings.map((warning, warningIndex) => (
                          <div key={warningIndex} className={`route-warning ${warning.type === 'germany' || warning.type === 'austria' ? 'positive' : ''}`}>
                            <span className="warning-icon">{warning.icon}</span>
                            <span className="warning-text">{warning.message}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="interrail-summary">
              <div className="summary-item">
                <span className="desktop-only">Interrail dagen: </span>
                <span className="mobile-only">🎫 </span>
                <strong>{interrailDays} dagen</strong>
              </div>
              <div className="summary-item">
                <span className="desktop-only">Overnachtingen: </span>
                <span className="mobile-only">🌙 </span>
                <strong>{routeOptions[0]?.waypoints?.filter(wp => wp.overnight).length || 0}</strong>
              </div>
            </div>
          </div>

          <div className="interrail-spacer"></div>

          <div className="interrail-booking">
            <a 
              href="https://www.awin1.com/cread.php?s=2830296&v=5797&q=408264&r=1693255" 
              rel="sponsored" 
              className="interrail-book-button"
            >
              <div className="interrail-logo-container">
                <span className="interrail-logo">🎫</span>
              </div>
              <div className="book-text-container">
                <span className="book-text">Koop pas</span>
              </div>
            </a>
            <a 
              href="/interrail/" 
              target="_blank" 
              className="interrail-info-link"
            >
              Lees meer over Interrail
            </a>
          </div>
        </div>
      </div>
    );
  };

  // Helper function to create geographically logical route with proper ordering
  const createGeographicRoute = (origin, destination, stations) => {
    if (stations.length === 0) return [];
    
    // For specific origin-destination pairs, use predefined logical routes
    const originLower = origin.toLowerCase();
    const destLower = destination.toLowerCase();
    
    // Dutch station to Poland (Katowice, Warsaw, etc.)
    if (originLower.includes('amsterdam') && 
        (destLower.includes('katowice') || destLower.includes('warschau') || destLower.includes('warsaw') || destLower.includes('poland'))) {
      // Look for Frankfurt(Oder) as the key border crossing
      const frankfurtOder = stations.find(s => s.borderCrossing === 'frankfurt-oder');
      if (frankfurtOder) {
        return [frankfurtOder.station];
      }
    }
    
    // Dutch station to Italy (Milaan, Rome, etc.)
    if (originLower.includes('amsterdam') && 
        (destLower.includes('milaan') || destLower.includes('milan') || destLower.includes('rome') || destLower.includes('bologna') || destLower.includes('italy'))) {
      // Look for Frankfurt and Basel in the correct order
      const frankfurt = stations.find(s => s.borderCrossing === 'frankfurt');
      const basel = stations.find(s => s.borderCrossing === 'basel');
      
      // Return in correct geographical order: Dutch station → Frankfurt → Basel → Milaan
      if (frankfurt && basel) {
        return [frankfurt.station, basel.station];
      } else if (frankfurt) {
        return [frankfurt.station];
      } else if (basel) {
        return [basel.station];
      }
    }
    
    // Dutch station to Austria (Vienna, etc.)
    if (originLower.includes('amsterdam') && 
        (destLower.includes('wenen') || destLower.includes('vienna') || destLower.includes('austria'))) {
      // Look for Frankfurt and Salzburg in correct order
      const frankfurt = stations.find(s => s.borderCrossing === 'frankfurt');
      const salzburg = stations.find(s => s.borderCrossing === 'salzburg');
      if (frankfurt && salzburg) {
        return [frankfurt.station, salzburg.station];
      } else if (frankfurt) {
        return [frankfurt.station];
      }
    }
    
    // Dutch station to Scandinavia
    if (originLower.includes('amsterdam') && 
        (destLower.includes('stockholm') || destLower.includes('oslo') || destLower.includes('kopenhagen'))) {
      const hamburg = stations.find(s => s.borderCrossing === 'hamburg');
      const kopenhagen = stations.find(s => s.borderCrossing === 'kopenhagen');
      if (hamburg && kopenhagen) {
        return [hamburg.station, kopenhagen.station];
      } else if (hamburg) {
        return [hamburg.station];
      }
    }

    // Dutch station to Spain (Barcelona, Madrid, etc.)
    if (originLower.includes('amsterdam') && 
        (destLower.includes('barcelona') || destLower.includes('madrid') || destLower.includes('valencia') || destLower.includes('spain'))) {
      // Look for Paris as the main hub - avoid backtracking
      const paris = stations.find(s => s.borderCrossing === 'parijs');
      if (paris) {
        return [paris.station];
      }
    }
    
    // Generic fallback: use border crossings in logical geographical order
    // Define the logical progression from north/west to south/east
    const borderCrossingOrder = [
      'hamburg',      // Northern Germany
      'kopenhagen',   // Denmark
      'frankfurt',    // Central Germany
      'frankfurt-oder', // German-Polish border
      'basel',        // German-Swiss border
      'salzburg',     // German-Austrian border
      'lyon',         // French-Italian border
      'geneve'        // French-Swiss border
    ];
    
    const selectedWaypoints = [];
    
    // For Dutch station to Spain/France, prioritize Paris
    if (originLower.includes('amsterdam') && 
        (destLower.includes('barcelona') || destLower.includes('madrid') || destLower.includes('paris') || destLower.includes('france'))) {
      const paris = stations.find(s => s.borderCrossing === 'parijs');
      if (paris) selectedWaypoints.push(paris.station);
    } else {
      // For other routes, use the general border crossing order
      for (const borderType of borderCrossingOrder) {
        const station = stations.find(s => s.borderCrossing === borderType);
        if (station && selectedWaypoints.length < 2) {
          selectedWaypoints.push(station.station);
        }
      }
    }
    
    // If no border crossings found, use the most common transfer stations (max 2)
    if (selectedWaypoints.length === 0) {
      return stations.slice(0, 2).map(s => s.station);
    }
    
    return selectedWaypoints;
  };

  return (
    <div>
      <div className="tripplanner-header">
        <h2>Reisplanner (bèta)</h2>
        <div className="search-form">
          <div className="key-input is-active search-stations" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
            <div className="station-row" style={{ flex: '1 1 45%', display: 'flex', alignItems: 'center' }}>
              <label className="input-label" style={{ visibility: 'hidden' }}>Van</label>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <FaTrain className="train-icon" />
                <div className="arrow-right"></div>
              </div>
              <CityPicker onSelect={handleOriginChange} placeholder={origin} />
            </div>
            <div className="swap-icon-container" style={{ flex: '0 0 auto', margin: '0 10px' }}>
              <FaExchangeAlt className="swap-icon" onClick={handleSwapLocations} />
            </div>
            <div className="station-row" style={{ flex: '1 1 45%', display: 'flex', alignItems: 'center' }}>
              <label className="input-label" style={{ visibility: 'hidden' }}>Naar</label>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <FaTrain className="train-icon" />
                <div className="arrow-left"></div>
              </div>
              <CityPicker onSelect={handleDestinationChange} placeholder={destination} />
            </div>
          </div>
          <div className="key-input is-active search-dates">
            <div className="date-rows-container">
              <div className={`date-row ${!isViewingReturn ? 'is-active' : ''}`}>
                <div className="previousDayIn" onClick={() => handleDateDelta(false, -1)}>&lsaquo;</div>
                <div className="date-display" onClick={() => handleDateDisplayClick(departureDateRef)}>
                  <label className="input-label">Heen</label>
                  <DatePicker
                    ref={departureDateRef}
                    selected={departureDate ? moment(departureDate, 'DD-MM-YYYY').toDate() : null}
                    onChange={(date) => {
                      handleDateChange(date, false);
                      setDepartureDatePickerOpen(false);
                    }}
                    value={getFormattedDateShorthand(departureDate)}
                    locale="nl"
                    open={departureDatePickerOpen}
                    onClickOutside={() => setDepartureDatePickerOpen(false)}
                    onInputClick={() => setDepartureDatePickerOpen(true)}
                    highlightDates={getHighlightedDates(origin, destination)} // Highlight for departure
                    dayClassName={(date) => getDayClassName(date, departureDate, getHighlightedDates(origin, destination))} // Pass highlightDates
                  />
                </div>
                <div className="nextDayIn" onClick={() => handleDateDelta(false, 1)}>&rsaquo;</div>
              </div>
              <div className={`date-row ${isViewingReturn ? 'is-active' : ''}`}>
                <div className="previousDayOut" onClick={() => handleDateDelta(true, -1)}>&lsaquo;</div>
                <div className="date-display" onClick={() => handleDateDisplayClick(returnDateRef)}>
                  <label className="input-label">Terug</label>
                  <DatePicker
                    ref={returnDateRef}
                    selected={returnDate ? moment(returnDate, 'DD-MM-YYYY').toDate() : null}
                    onChange={(date) => {
                      handleDateChange(date, true);
                      setReturnDatePickerOpen(false);
                    }}
                    value={getFormattedDateShorthand(returnDate)}
                    locale="nl"
                    open={returnDatePickerOpen}
                    onClickOutside={() => setReturnDatePickerOpen(false)}
                    onInputClick={() => setReturnDatePickerOpen(true)}
                    highlightDates={getHighlightedDates(destination, origin)} // Highlight for return, swap origin and destination
                    dayClassName={(date) => getDayClassName(date, returnDate, getHighlightedDates(destination, origin))} // Pass highlightDates
                  />
                </div>
                <div className="nextDayOut" onClick={() => handleDateDelta(true, 1)}>&rsaquo;</div>
              </div>
            </div>
            <div className="break"></div>
            <div className="moveDateAhead">
              <span className="tip">Tip &mdash; zoek goedkoop: </span>
              <span className="dateAheadButton" onClick={() => handleDateDelta(false, 60, true)}>nu + 2 maanden</span>
              <span className="dateAheadButton" onClick={() => handleDateDelta(false, 90, true)}>nu + 3 maanden</span>
              {returnDate && (
                <span 
                  className="dateAheadButton"
                  onClick={() => {
                    handleSwapLocations(true); // Clear saved trips for this action
                    setIsViewingReturn(!isViewingReturn);
                    setTravelDate(!isViewingReturn ? returnDate : departureDate);
                    resetStateAndClearSavedTrips();
                  }}
                >
                  {isViewingReturn ? "bekijk heenreis" : "bekijk terugreis"}
                </span>
              )}
            </div>
          </div>
          <div className="key-input is-active search-passengers">
            <div className="passenger-selector">
              {(!isPassengerExpanded && isMobile) && (
                <div 
                  className="passenger-collapsed-view" 
                  onClick={() => setIsPassengerExpanded(!isPassengerExpanded)}
                >
                  <div className="passenger-collapsed-content">
                    <div className="passenger-collapsed-text">
                      {passengerCounts.adults} volwassene
                    </div>
                    <div className={`passenger-collapsed-arrow ${isPassengerExpanded ? 'expanded' : ''}`}>
                      ▼
                    </div>
                  </div>
                </div>
              )}
              <div className={`passenger-sub-categories ${isPassengerExpanded || !isMobile ? 'expanded' : ''}`}>
                <div className="passenger-sub-category">
                  <div className="circle-container">
                    <button 
                      className="passenger-button large" 
                      onClick={() => updatePassengerCount('adults', -1)}
                      disabled={passengerCounts.adults <= 0}
                    >-</button>
                    <div className="passenger-circle">{passengerCounts.adults}</div>
                    <button 
                      className="passenger-button large" 
                      onClick={() => updatePassengerCount('adults', 1)}
                      disabled={passengerCounts.adults >= 9}
                    >+</button>
                  </div>
                  <span className="passenger-label">Volwassene<br/><span className="age-range">26 - 59</span></span>
                </div>
                {[
                  { label: 'Baby', age: '0 - 3', key: 'babies' },
                  { label: 'Kind', age: '4 - 5', key: 'children4to5' },
                  { label: 'Kind', age: '6 - 11', key: 'children6to11' },
                  { label: 'Kind', age: '12 - 14', key: 'children12to14' },
                  { label: 'Jongere', age: '15', key: 'youth15' },
                  { label: 'Jongere', age: '16 - 25', key: 'youth16to25' },
                  { label: 'Senior', age: '65+', key: 'seniors' },
                ].map(({ label, age, key }) => (
                  <div key={key} className="passenger-sub-category">
                    <div className="circle-container">
                      <button 
                        className="passenger-button" 
                        onClick={() => updatePassengerCount(key, -1)}
                        disabled={passengerCounts[key] <= 0}
                      >-</button>
                      <div className="passenger-circle-small" data-value={passengerCounts[key]}>{passengerCounts[key]}</div>
                      <button 
                        className="passenger-button" 
                        onClick={() => updatePassengerCount(key, 1)}
                        disabled={passengerCounts[key] >= 9}
                      >+</button>
                    </div>
                    <span>{label}<br/><span className="age-range">{age}</span></span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div ref={scrollRef}></div>
      
      {/* Responsive layout wrapper */}
      <div className="trip-results-layout">
        {/* Main content area */}
        <div className="trip-main-content">
          <div className="sort-buttons">
            <button className={`sort-button ${sortOption === 'best' ? 'active' : ''}`} onClick={() => setSortOption('best')}>Beste</button>
            <button className={`sort-button ${sortOption === 'cheapest' ? 'active' : ''}`} onClick={() => setSortOption('cheapest')}>Prijs</button>
            <button className={`sort-button ${sortOption === 'fastest' ? 'active' : ''}`} onClick={() => setSortOption('fastest')}>Snelste</button>
            <button className={`sort-button ${sortOption === 'chronological' ? 'active' : ''}`} onClick={() => setSortOption('chronological')}>Vertrek</button>
          </div>
          
          {/* Filter labels - visible on both mobile and desktop */}
          <div className="active-filters">
            <FilterLabels />
          </div>
          
          {/* Filter toggle button - only visible on mobile/tablet */}
          <div className="filter-row mobile-only">
            <button 
              className={`filter-toggle ${isFilterPanelOpen ? 'active' : ''}`}
              onClick={() => setIsFilterPanelOpen(!isFilterPanelOpen)}
            >
              Filters&nbsp;{isFilterPanelOpen ? '▲' : '▼'}
            </button>
          </div>
          
          
          {/* Mobile filter panel - only visible on mobile/tablet when open */}
          {isFilterPanelOpen && (
            <div className="filter-container mobile-only">
              <div className="filter-panel">
                <FilterPanel />
              </div>
            </div>
          )}
          
          {(loading || isLoadingMore || loadingState !== 'hidden') && (
            <div className={`loading-more-overlay ${loadingState === 'hiding' ? 'hiding' : ''}`}>
              <div className="loading-more">
                <div className={`loading-indicator ${loadingState === 'complete' || loadingState === 'hiding' ? 'complete' : ''}`}>
                  <div className="loading-spinner"></div>
                  <svg className="checkmark" viewBox="0 0 24 24">
                    <path d="M4.1 12.7L9 17.6 20.3 6.3" fill="none" />
                  </svg>
                </div>
                <span>
                  {(loadingState === 'complete' || loadingState === 'hiding') ? 'Alle opties geladen' : 
                   tripOptions.length === 0 ? 'Reisopties laden...' : 
                   requestPhase === 'extra' ? 'Extra reisopties laden...' :
                   'Meer reisopties laden...'}
                </span>
              </div>
            </div>
          )}

          {!loading && !isLoadingMore && !tripOptions.length && (
            <div>Helaas konden we geen routes vinden op deze datum.</div>
          )}

          {tripOptions.length > 0 && (
            <div className="trip-container">
              {(() => {
                // Check if shortest trip is over 10 hours
                const filteredTrips = filterTrips(tripOptions);
                if (filteredTrips.length === 0) return null;
                
                const shortestTrip = filteredTrips.reduce((shortest, current) => {
                  const shortestDuration = new Date(shortest.arrival) - new Date(shortest.departure);
                  const currentDuration = new Date(current.arrival) - new Date(current.departure);
                  return currentDuration < shortestDuration ? current : shortest;
                });
                
                const shortestDurationHours = (new Date(shortestTrip.arrival) - new Date(shortestTrip.departure)) / (1000 * 60 * 60);
                
                // Show Interrail recommendation if shortest trip is over 10 hours
                // Hide Interrail recommendation when saved trips are being shown (user chose a specific trip)
                if (shortestDurationHours > 10 && 
                    (loadingState === 'complete' || loadingState === 'hiding' || loadingState === 'hidden') &&
                    tripOptions.length >= 5 && 
                    !savedOutboundTrip) { // Ensure we have enough trip data for meaningful analysis
                  const routeOptions = calculateInterrailWaypoints(origin, destination, tripOptions);
                  
                  // Only show Interrail recommendation if there are routes with overnight stays
                  if (routeOptions.length > 0) {
                    const interrailDaysNeeded = calculateInterrailDays(routeOptions[0]?.waypoints || []);
                    
                    return (
                      <InterrailRecommendation 
                        origin={origin}
                        destination={destination}
                        routeOptions={routeOptions}
                        interrailDays={interrailDaysNeeded}
                        isLongTrip={true}
                      />
                    );
                  }
                }
                
                // Show Interrail recommendation for scenic routes on shorter trips
                // Hide Interrail recommendation when saved trips are being shown (user chose a specific trip)
                if (shortestDurationHours <= 10 && 
                    (loadingState === 'complete' || loadingState === 'hiding' || loadingState === 'hidden') &&
                    tripOptions.length >= 5 && 
                    !savedOutboundTrip) { // Ensure we have enough trip data for meaningful analysis
                  const routeOptions = calculateInterrailWaypoints(origin, destination, tripOptions);
                  
                  // Only show scenic routes for shorter trips
                  const scenicRoutesOnly = routeOptions.filter(route => route.type === 'scenic');
                  
                  if (scenicRoutesOnly.length > 0) {
                    const interrailDaysNeeded = calculateInterrailDays(scenicRoutesOnly[0]?.waypoints || []);
                    
                    return (
                      <InterrailRecommendation 
                        origin={origin}
                        destination={destination}
                        routeOptions={scenicRoutesOnly}
                        interrailDays={interrailDaysNeeded}
                        isLongTrip={false}
                      />
                    );
                  }
                }
                
                return null;
              })()}
              
              {/* Display saved trips */}
              <div ref={savedTripsContainerRef}>
                {savedOutboundTrip && (
                  <div className="trip-option saved-trip">
                  <div className="saved-trip-label">Opgeslagen heenreis</div>
                  <div className="saved-trip-date-header">
                    {getFormattedDateShorthand(departureDate)}
                  </div>
                  <div className="trip-option-content saved-trip-content">
                    <div className="trip-time">
                      <span className="trip-time-city">{shorterStationName(savedOutboundTrip.legs[0].origin)}</span>
                      {formatTime(savedOutboundTrip.departure)}
                    </div>
                    <div className="trip-journey" ref={journeyRef}>
                      {savedOutboundTrip.legs.map((leg, legIndex) => {
                        const tripDuration = calculateDuration(savedOutboundTrip.departure, savedOutboundTrip.arrival);
                        const legDuration = calculateDuration(leg.departure, leg.arrival);
                        const legDurationRatio = legDuration / tripDuration;
                        let legWidth = getWidth(legDuration, tripDuration);
                        const lineHeight = legIndex < savedOutboundTrip.legs.length
                          ? Math.max(legDurationRatio * 400, 20)
                          : 0;
                        const isWalking = (leg.operator).split(',', 1)[0] === "N/A";
                        const transferDuration = legIndex > 0 ? calculateTransferTime(savedOutboundTrip.legs[legIndex - 1].arrival, leg.departure) : 0;
                        const transferWidthInPixels = (transferDuration / tripDuration) * journeyWidthInPixels;
                        const transferWidthPercentage = (transferDuration / tripDuration) * 100;
                        const transferDurationRatio = transferDuration / tripDuration;
                        const transferHeight = transferDurationRatio * 400;
                        const transfer = calculateTransferTimePretty(savedOutboundTrip.legs[legIndex - 1]?.arrival, leg.departure);
                        const circleSize = 10;
                        const transferDetailsTop = circleSize + (lineHeight / 2);

                        const minTransferWidth = 30;
                        const minTransferHeight = 30;
                        const transferClass = !isMobile
                          ? (transferWidthInPixels > minTransferWidth ? 'rounded-rectangle' : 'circle-shape')
                          : (transferHeight > minTransferHeight ? 'rounded-rectangle-vertical' : 'circle-shape');

                        const circleStyle = {
                          width: `${circleSize}px`,
                          height: `${circleSize}px`,
                        };
                        
                        if (transferClass === 'rounded-rectangle') {
                          circleStyle.width = `${transferWidthPercentage}%`;
                        } else if (transferClass === 'rounded-rectangle-vertical') {
                          circleStyle.height = `${transferHeight}px`;
                        }

                        const moveLabelRight = {
                          marginLeft: transferClass === 'rounded-rectangle' ? `${transferWidthInPixels / 4 - 10}px` : '0',
                        };

                        legWidth += transferWidthPercentage;

                        const TrainTypeDisplay = ({ product }) => {
                          const trainTypeMap = {
                            regional: "Stoptrein",
                            national: "Intercity",
                            regionalExpress: "Sneltrein",
                            nationalExpress: "Hogesnelheidstrein",
                            ferry: "Veerboot",
                            suburban: "Stedelijk vervoer",
                            "N/A": "Ander vervoer",
                          };
                        
                          const trainType = trainTypeMap[product];
                        
                          if (!trainType) {
                            return null;
                          }

                          if (leg.train_number?.startsWith('NJ ') || (leg.operator).split(',', 1)[0] === "European Sleeper" || (leg.operator).split(',', 1)[0] === "Urlaubs-Express") {
                            return <span>Nachttrein</span>;
                          }
                        
                          return <span>{trainType}</span>;
                        };

                        return (
                          <div className="journey-leg" key={legIndex} style={{ width: isMobile ? '100%' : `${legWidth}%` }}>
                            <div className={`circle ${transferClass}`} style={{
                              ...circleStyle,
                              '--transfer-height': `${transferHeight}px`
                            }}>
                              <div className="station-name" title={shorterStationName(leg.origin)}>
                                {shorterStationName(leg.origin)}
                              </div>
                              {!isWalking && legIndex > 0 && legIndex < savedOutboundTrip.legs.length && transfer.formattedTime !== "0m" && (
                                <div className={`transfer-details ${transfer.colorClass}`}>
                                  <div className="transfer-icon"></div>
                                  <div className="transfer-time">
                                    {transfer.formattedTime}
                                  </div>
                                </div>
                              )}
                            </div>
                            {legIndex < savedOutboundTrip.legs.length && (
                              <div
                                className={isWalking ? "solid-line" : "dashed-line"}
                                style={{ 
                                  height: isMobile ? `${lineHeight}px` : '1px',
                                  '--line-height': `${lineHeight}px`
                                }}
                              ></div>
                            )}
                            <div
                              className={`leg-details ${isWalking ? 'is-walking' : ''}`}
                              style={
                                isMobile
                                  ? transferDetailsTop > 25
                                    ? {
                                        opacity: 1,
                                        position: 'absolute',
                                        top: `${(legDurationRatio + 0.25) * 100 + 0.75 * (transferHeight - 25) + 15}px`,
                                        left: '35px', // Adjust as needed
                                        transform: 'translateY(-50%)', // Centers the div vertically
                                      }
                                    : {
                                        opacity: 1,
                                        position: 'absolute',
                                        top: `${transferDetailsTop + 15 + 0.75 * (transferHeight - 10)}px`,
                                        left: '42px', // Adjust as needed
                                        transform: 'translateY(-50%)', // Centers the div vertically
                                        padding: '0',
                                        border: '0',
                                        boxShadow: 'none',
                                        fontSize: '0.8rem',
                                        background: 'none',
                                      }
                                  : moveLabelRight
                              }
                            >
                              {isWalking ? (
                                <div className="walking-icon">🚶</div>
                              ) : (
                                <>
                                  {leg.operator.includes("Nederlandse Spoorwegen") ? (
                                    <img src={`${baseUrl}/icons/nederlandsespoorwegen.png`} alt="Nederlandse Spoorwegen" className="operator-icon" />
                                  ) : (leg.operator).split(',', 1)[0].startsWith("Bus") ? (
                                    <div className="bus-icon">🚌</div>
                                  ) : (
                                    <img src={`${baseUrl}/icons/${(leg.operator).split(',', 1)[0].replace(/\s+/g, '').toLowerCase()}.png`} alt={(leg.operator).split(',', 1)[0]} className="operator-icon" />
                                  )}
                                </>
                              )}
                              <div className="leg-duration">{calculateDurationPretty(leg.departure, leg.arrival)}</div>
                              <div className="leg-product"><TrainTypeDisplay product={leg.product} /></div>
                            </div>
                          </div>
                        );
                      })}
                      <div className="circle">
                        <div className="station-name">{shorterStationName(savedOutboundTrip.legs[savedOutboundTrip.legs.length - 1].destination)}</div>
                      </div>
                    </div>
                    <div className="trip-time">
                      {new Date(savedOutboundTrip.arrival).getDate() !== new Date(savedOutboundTrip.departure).getDate() && <span className="next-day-icon">☾</span>}
                      <span className="trip-time-city">{shorterStationName(savedOutboundTrip.legs[savedOutboundTrip.legs.length - 1].destination)}</span>
                      {formatTime(savedOutboundTrip.arrival)}
                      {new Date(savedOutboundTrip.arrival).getDate() !== new Date(savedOutboundTrip.departure).getDate() && <span className="next-day">Volgende dag</span>}
                    </div>

                    <div className="vertical-dash"></div>
                    <div className="trip-price">
                      {savedOutboundTrip.price && savedOutboundTrip.price.amount > 0 && (
                        <div className="trip-price-amount">
                          €{Math.floor(savedOutboundTrip.price.amount).toLocaleString()}<sup>,{(savedOutboundTrip.price.amount % 1).toFixed(2).substring(2)}</sup>
                        </div>
                      )}
                      <div className="price-terms">
                        <span className="price-single">Opgeslagen heenreis</span>
                      </div>
                      {(() => {
                        const compositeKey = createCompositeKey(savedOutboundTrip.departure, savedOutboundTrip.arrival);
                        const tripDetails = availableTrips[compositeKey] || {};
                        const { vendors, bookingUrls } = tripDetails;
                        
                        return vendors && vendors.length > 0 && (
                          <div className="trip-tickets">
                            <div className="ticket-label">
                              <span className="tickets">TICKETS</span>
                            </div>
                            <div className="vendor-list">
                              {vendors
                                .filter(vendor => filters.vendors[vendor])
                                .map((vendor, idx) => {
                                  let vendorLogo = "";
                                  if (vendor === "NS") {
                                    vendorLogo = "nederlandsespoorwegen";
                                  } else if (vendor === "DB") {
                                    vendorLogo = "dbfernverkehrag";
                                  } else if (vendor === "European Sleeper") {
                                    vendorLogo = "europeansleeper";
                                  }

                                  return (
                                    <React.Fragment key={`saved-outbound-${vendor}-${idx}`}>
                                      <div
                                        className={`vendor-box ${idx <= 1 ? 'auto-expanded' : visibleVendorBoxes[compositeKey] ? 'expanded' : ''}`}
                                        ref={el => vendorBoxRefs.current[compositeKey] = el}
                                      >
                                        <a href={bookingUrls?.[vendor]} target="_blank" rel="sponsored nofollow">
                                          <div className="vendor-logo-container">
                                            <img src={`${baseUrl}/icons/${vendorLogo}.png`} alt="Vendor Logo" className="vendor-logo" />
                                          </div>
                                          <div className="book-text-container">
                                            <span className="book-text">Boeken</span>
                                          </div>
                                        </a>
                                        {vendor === 'DB' && savedOutboundTrip.via && (
                                          <div className="experimental-notice">
                                            Experimentele functie - Controleer de route op de DB-website!
                                          </div>
                                        )}
                                      </div>
                                    </React.Fragment>
                                  );
                                })}
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    <div className="trip-summary">
                      <div className="summary-item">
                        <span className="desktop-only">Reisduur: </span>
                        <span className="mobile-only">⏱️ </span>
                        <strong>{formatDuration(savedOutboundTrip)}</strong>
                      </div>
                      <div className="summary-item">
                        <span className="desktop-only">Overstappen: </span>
                        <span className="mobile-only">🔄 </span>
                        <strong>{savedOutboundTrip.legs.length - 1}</strong>
                      </div>
                      <div className="summary-item complexity-indicator">
                        {(savedOutboundTrip.complexity?.value || 1) > 1 ? (
                          <div 
                            className="complexity-clickable"
                            title={savedOutboundTrip.complexity?.tooltip}
                            onClick={(e) => {
                              e.stopPropagation();
                              setMobileTooltipText(savedOutboundTrip.complexity?.tooltip);
                              setMobileTooltipVisible(true);
                            }}
                          >
                            <span className={`complexity-dot complexity-dot-${savedOutboundTrip.complexity?.value || 1}`}></span>
                            <span className="complexity-label">{getComplexityLabel(savedOutboundTrip.complexity?.value || 1)}</span>
                            <span className="complexity-tooltip">ⓘ</span>
                          </div>
                        ) : (
                          <>
                            <span className={`complexity-dot complexity-dot-${savedOutboundTrip.complexity?.value || 1}`}></span>
                            <span className="complexity-label">{getComplexityLabel(savedOutboundTrip.complexity?.value || 1)}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="trip-remarks">
                      {(() => {
                        const hasEuropeanSleeperLeg = savedOutboundTrip.legs.some(leg => 
                          (leg.operator).split(',', 1)[0] === "European Sleeper"
                        );
                        const isMultiLegJourney = savedOutboundTrip.legs.length > 1;
                        const shouldShowWarning = hasEuropeanSleeperLeg && isMultiLegJourney;

                        const europeanSleeperLeg = savedOutboundTrip.legs.find(leg => 
                          (leg.operator).split(',', 1)[0] === "European Sleeper"
                        );

                        const europeanSleeperStation = europeanSleeperLeg ? europeanSleeperLeg.origin : "het European Sleeper-station";

                        return shouldShowWarning && (
                          <div className="remark">
                            &#9432; European Sleeper verkoopt alleen rechtstreekse tickets. Overweeg om een los kaartje tot aan {europeanSleeperStation} te kopen. Zorg wel dat je daar op tijd bent, aangezien de overstap dan voor eigen risico komt.
                          </div>
                        );
                      })()}
                      {savedOutboundTrip.legs.some(leg => 
                        (leg.operator).split(',', 1)[0] === "European Sleeper" || 
                        (leg.operator).split(',', 1)[0] === "Urlaubs-Express" ||
                        leg.train_number?.startsWith('NJ ')
                      ) && (
                        <div className="remark">&#9432; Deze reis bevat een slaaptrein. Lees onze <a href="/hoe-werkt-een-nachttrein/" target="_blank">informatie over nachttreinen</a>.</div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Display saved return trip */}
              {savedReturnTrip && (
                <div className="trip-option saved-trip">
                  <div className="saved-trip-label">Opgeslagen terugreis</div>
                  <div className="saved-trip-date-header">
                    {getFormattedDateShorthand(returnDate)}
                  </div>
                  <div className="trip-option-content saved-trip-content">
                    <div className="trip-time">
                      <span className="trip-time-city">{shorterStationName(savedReturnTrip.legs[0].origin)}</span>
                      {formatTime(savedReturnTrip.departure)}
                    </div>
                    <div className="trip-journey" ref={journeyRef}>
                      {savedReturnTrip.legs.map((leg, legIndex) => {
                        const tripDuration = calculateDuration(savedReturnTrip.departure, savedReturnTrip.arrival);
                        const legDuration = calculateDuration(leg.departure, leg.arrival);
                        const legDurationRatio = legDuration / tripDuration;
                        let legWidth = getWidth(legDuration, tripDuration);
                        const lineHeight = legIndex < savedReturnTrip.legs.length
                          ? Math.max(legDurationRatio * 400, 20)
                          : 0;
                        const isWalking = (leg.operator).split(',', 1)[0] === "N/A";
                        const transferDuration = legIndex > 0 ? calculateTransferTime(savedReturnTrip.legs[legIndex - 1].arrival, leg.departure) : 0;
                        const transferWidthInPixels = (transferDuration / tripDuration) * journeyWidthInPixels;
                        const transferWidthPercentage = (transferDuration / tripDuration) * 100;
                        const transferDurationRatio = transferDuration / tripDuration;
                        const transferHeight = transferDurationRatio * 400;
                        const transfer = calculateTransferTimePretty(savedReturnTrip.legs[legIndex - 1]?.arrival, leg.departure);
                        const circleSize = 10;
                        const transferDetailsTop = circleSize + (lineHeight / 2);

                        const minTransferWidth = 30;
                        const minTransferHeight = 30;
                        const transferClass = !isMobile
                          ? (transferWidthInPixels > minTransferWidth ? 'rounded-rectangle' : 'circle-shape')
                          : (transferHeight > minTransferHeight ? 'rounded-rectangle-vertical' : 'circle-shape');

                        const circleStyle = {
                          width: `${circleSize}px`,
                          height: `${circleSize}px`,
                        };
                        
                        if (transferClass === 'rounded-rectangle') {
                          circleStyle.width = `${transferWidthPercentage}%`;
                        } else if (transferClass === 'rounded-rectangle-vertical') {
                          circleStyle.height = `${transferHeight}px`;
                        }

                        const moveLabelRight = {
                          marginLeft: transferClass === 'rounded-rectangle' ? `${transferWidthInPixels / 4 - 10}px` : '0',
                        };

                        legWidth += transferWidthPercentage;

                        const TrainTypeDisplay = ({ product }) => {
                          const trainTypeMap = {
                            regional: "Stoptrein",
                            national: "Intercity",
                            regionalExpress: "Sneltrein",
                            nationalExpress: "Hogesnelheidstrein",
                            ferry: "Veerboot",
                            suburban: "Stedelijk vervoer",
                            "N/A": "Ander vervoer",
                          };
                        
                          const trainType = trainTypeMap[product];
                        
                          if (!trainType) {
                            return null;
                          }

                          if (leg.train_number?.startsWith('NJ ') || (leg.operator).split(',', 1)[0] === "European Sleeper" || (leg.operator).split(',', 1)[0] === "Urlaubs-Express") {
                            return <span>Nachttrein</span>;
                          }
                        
                          return <span>{trainType}</span>;
                        };

                        return (
                          <div className="journey-leg" key={legIndex} style={{ width: isMobile ? '100%' : `${legWidth}%` }}>
                            <div className={`circle ${transferClass}`} style={{
                              ...circleStyle,
                              '--transfer-height': `${transferHeight}px`
                            }}>
                              <div className="station-name" title={shorterStationName(leg.origin)}>
                                {shorterStationName(leg.origin)}
                              </div>
                              {!isWalking && legIndex > 0 && legIndex < savedReturnTrip.legs.length && transfer.formattedTime !== "0m" && (
                                <div className={`transfer-details ${transfer.colorClass}`}>
                                  <div className="transfer-icon"></div>
                                  <div className="transfer-time">
                                    {transfer.formattedTime}
                                  </div>
                                </div>
                              )}
                            </div>
                            {legIndex < savedReturnTrip.legs.length && (
                              <div
                                className={isWalking ? "solid-line" : "dashed-line"}
                                style={{ 
                                  height: isMobile ? `${lineHeight}px` : '1px',
                                  '--line-height': `${lineHeight}px`
                                }}
                              ></div>
                            )}
                            <div
                              className={`leg-details ${isWalking ? 'is-walking' : ''}`}
                              style={
                                isMobile
                                  ? transferDetailsTop > 25
                                    ? {
                                        opacity: 1,
                                        position: 'absolute',
                                        top: `${(legDurationRatio + 0.25) * 100 + 0.75 * (transferHeight - 25) + 15}px`,
                                        left: '35px', // Adjust as needed
                                        transform: 'translateY(-50%)', // Centers the div vertically
                                      }
                                    : {
                                        opacity: 1,
                                        position: 'absolute',
                                        top: `${transferDetailsTop + 15 + 0.75 * (transferHeight - 10)}px`,
                                        left: '42px', // Adjust as needed
                                        transform: 'translateY(-50%)', // Centers the div vertically
                                        padding: '0',
                                        border: '0',
                                        boxShadow: 'none',
                                        fontSize: '0.8rem',
                                        background: 'none',
                                      }
                                  : moveLabelRight
                              }
                            >
                              {isWalking ? (
                                <div className="walking-icon">🚶</div>
                              ) : (
                                <>
                                  {leg.operator.includes("Nederlandse Spoorwegen") ? (
                                    <img src={`${baseUrl}/icons/nederlandsespoorwegen.png`} alt="Nederlandse Spoorwegen" className="operator-icon" />
                                  ) : (leg.operator).split(',', 1)[0].startsWith("Bus") ? (
                                    <div className="bus-icon">🚌</div>
                                  ) : (
                                    <img src={`${baseUrl}/icons/${(leg.operator).split(',', 1)[0].replace(/\s+/g, '').toLowerCase()}.png`} alt={(leg.operator).split(',', 1)[0]} className="operator-icon" />
                                  )}
                                </>
                              )}
                              <div className="leg-duration">{calculateDurationPretty(leg.departure, leg.arrival)}</div>
                              <div className="leg-product"><TrainTypeDisplay product={leg.product} /></div>
                            </div>
                          </div>
                        );
                      })}
                      <div className="circle">
                        <div className="station-name">{shorterStationName(savedReturnTrip.legs[savedReturnTrip.legs.length - 1].destination)}</div>
                      </div>
                    </div>
                    <div className="trip-time">
                      {new Date(savedReturnTrip.arrival).getDate() !== new Date(savedReturnTrip.departure).getDate() && <span className="next-day-icon">☾</span>}
                      <span className="trip-time-city">{shorterStationName(savedReturnTrip.legs[savedReturnTrip.legs.length - 1].destination)}</span>
                      {formatTime(savedReturnTrip.arrival)}
                      {new Date(savedReturnTrip.arrival).getDate() !== new Date(savedReturnTrip.departure).getDate() && <span className="next-day">Volgende dag</span>}
                    </div>

                    <div className="vertical-dash"></div>
                    <div className="trip-price">
                      {savedReturnTrip.price && savedReturnTrip.price.amount > 0 && (
                        <div className="trip-price-amount">
                          €{Math.floor(savedReturnTrip.price.amount).toLocaleString()}<sup>,{(savedReturnTrip.price.amount % 1).toFixed(2).substring(2)}</sup>
                        </div>
                      )}
                      <div className="price-terms">
                        <span className="price-single">Opgeslagen terugreis</span>
                      </div>
                      {(() => {
                        const compositeKey = createCompositeKey(savedReturnTrip.departure, savedReturnTrip.arrival);
                        const tripDetails = availableTrips[compositeKey] || {};
                        const { vendors, bookingUrls } = tripDetails;
                        
                        return vendors && vendors.length > 0 && (
                          <div className="trip-tickets">
                            <div className="ticket-label">
                              <span className="tickets">TICKETS</span>
                            </div>
                            <div className="vendor-list">
                              {vendors
                                .filter(vendor => filters.vendors[vendor])
                                .map((vendor, idx) => {
                                  let vendorLogo = "";
                                  if (vendor === "NS") {
                                    vendorLogo = "nederlandsespoorwegen";
                                  } else if (vendor === "DB") {
                                    vendorLogo = "dbfernverkehrag";
                                  } else if (vendor === "European Sleeper") {
                                    vendorLogo = "europeansleeper";
                                  }

                                  return (
                                    <React.Fragment key={`saved-return-${vendor}-${idx}`}>
                                      <div
                                        className={`vendor-box ${idx <= 1 ? 'auto-expanded' : visibleVendorBoxes[compositeKey] ? 'expanded' : ''}`}
                                        ref={el => vendorBoxRefs.current[compositeKey] = el}
                                      >
                                        <a href={bookingUrls?.[vendor]} target="_blank" rel="sponsored nofollow">
                                          <div className="vendor-logo-container">
                                            <img src={`${baseUrl}/icons/${vendorLogo}.png`} alt="Vendor Logo" className="vendor-logo" />
                                          </div>
                                          <div className="book-text-container">
                                            <span className="book-text">Boeken</span>
                                          </div>
                                        </a>
                                        {vendor === 'DB' && savedReturnTrip.via && (
                                          <div className="experimental-notice">
                                            Experimentele functie - Controleer de route op de DB-website!
                                          </div>
                                        )}
                                      </div>
                                    </React.Fragment>
                                  );
                                })}
                            </div>
                          </div>
                        );
                      })()}
                    </div>

                    <div className="trip-summary">
                      <div className="summary-item">
                        <span className="desktop-only">Reisduur: </span>
                        <span className="mobile-only">⏱️ </span>
                        <strong>{formatDuration(savedReturnTrip)}</strong>
                      </div>
                      <div className="summary-item">
                        <span className="desktop-only">Overstappen: </span>
                        <span className="mobile-only">🔄 </span>
                        <strong>{savedReturnTrip.legs.length - 1}</strong>
                      </div>
                      <div className="summary-item complexity-indicator">
                        {(savedReturnTrip.complexity?.value || 1) > 1 ? (
                          <div 
                            className="complexity-clickable"
                            title={savedReturnTrip.complexity?.tooltip}
                            onClick={(e) => {
                              e.stopPropagation();
                              setMobileTooltipText(savedReturnTrip.complexity?.tooltip);
                              setMobileTooltipVisible(true);
                            }}
                          >
                            <span className={`complexity-dot complexity-dot-${savedReturnTrip.complexity?.value || 1}`}></span>
                            <span className="complexity-label">{getComplexityLabel(savedReturnTrip.complexity?.value || 1)}</span>
                            <span className="complexity-tooltip">ⓘ</span>
                          </div>
                        ) : (
                          <>
                            <span className={`complexity-dot complexity-dot-${savedReturnTrip.complexity?.value || 1}`}></span>
                            <span className="complexity-label">{getComplexityLabel(savedReturnTrip.complexity?.value || 1)}</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="trip-remarks">
                      {(() => {
                        const hasEuropeanSleeperLeg = savedReturnTrip.legs.some(leg => 
                          (leg.operator).split(',', 1)[0] === "European Sleeper"
                        );
                        const isMultiLegJourney = savedReturnTrip.legs.length > 1;
                        const shouldShowWarning = hasEuropeanSleeperLeg && isMultiLegJourney;

                        const europeanSleeperLeg = savedReturnTrip.legs.find(leg => 
                          (leg.operator).split(',', 1)[0] === "European Sleeper"
                        );

                        const europeanSleeperStation = europeanSleeperLeg ? europeanSleeperLeg.destination : "het European Sleeper-station";

                        return shouldShowWarning && (
                          <div className="remark">
                            &#9432; European Sleeper verkoopt alleen rechtstreekse tickets. Overweeg om een los kaartje vanaf {europeanSleeperStation} te kopen.
                          </div>
                        );
                      })()}
                      {savedReturnTrip.legs.some(leg => 
                        (leg.operator).split(',', 1)[0] === "European Sleeper" || 
                        (leg.operator).split(',', 1)[0] === "Urlaubs-Express" ||
                        leg.train_number?.startsWith('NJ ')
                      ) && (
                        <div className="remark">&#9432; Deze reis bevat een slaaptrein. Lees onze <a href="/hoe-werkt-een-nachttrein/" target="_blank">informatie over nachttreinen</a>.</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              </div>

              {/* Spacer between saved trips and regular trips */}
              {savedOutboundTrip && !isShowingSavedTripsOnly && (
                <div className="saved-trips-spacer">
                  <div className="spacer-line"></div>
                  <div className="spacer-text">Opties terugreis {getFormattedDateShorthand(returnDate)}</div>
                  <div className="spacer-line"></div>
                </div>
              )}

              {/* Clear saved trips button when showing saved trips only */}
              {isShowingSavedTripsOnly && (
                <div className="clear-saved-trips-container">
                  <button 
                    className="print-saved-trips-button"
                    onClick={captureSavedTripsScreenshot}
                  >
                    Screenshot opgeslagen reizen
                  </button>
                  <button 
                    className="clear-saved-trips-button"
                    onClick={() => {
                      clearSavedTrips();
                    }}
                  >
                    Opgeslagen reizen wissen
                  </button>
                </div>
              )}
              
              {!isShowingSavedTripsOnly && filterTrips(tripOptions)
                .slice()
                .sort((a, b) => {
                  const getDuration = (trip) => new Date(trip.arrival) - new Date(trip.departure);

                  if (sortOption === 'best') {
                    // Calculate effective duration with penalties based on legs
                    const calculateEffectiveDuration = (trip) => {
                      const baseDuration = getDuration(trip);
                      const departureHour = new Date(trip.departure).getHours();
                      const arrivalHour = new Date(trip.arrival).getHours();
                      const nextDayArrival = () => {
                        return Math.ceil((new Date(trip.arrival) - new Date(trip.departure)) / (24 * 60 * 60 * 1000));
                      };

                      let penalty = 0;

                      const legs = Array.isArray(trip.legs) ? trip.legs : [];
                      
                      // Calculate transfer penalties
                      for (let i = 0; i < legs.length - 1; i++) {
                        const currentLeg = legs[i];
                        const nextLeg = legs[i + 1];
                        const transferDuration = new Date(nextLeg.departure) - new Date(currentLeg.arrival);
                        const transferHour = new Date(currentLeg.arrival).getHours();

                        if (transferDuration > 60 * 60 * 1000) { // over 1 hour
                          penalty += 0.2 * baseDuration + transferDuration;
                          if (transferHour >= 0 && transferHour < 7) {
                            penalty *= 4; // Long nightly transfer gives extra large penalty
                          }
                        } else {
                          penalty += 0.15 * baseDuration;
                          if (transferHour >= 0 && transferHour < 7) {
                            penalty *= 3; // Short nightly transfer gives large penalty
                          }
                        }

                        if (departureHour > 21 || departureHour < 6) {
                          penalty += 0.6 * baseDuration;
                        }

                        if (arrivalHour > 23 || arrivalHour < 7) {
                          penalty += 0.4 * baseDuration;
                        }

                        const daysArrivingNext = nextDayArrival();
                        if (daysArrivingNext > 0) {
                          penalty += 0.6 * daysArrivingNext * baseDuration;
                        }
                      }

                      return baseDuration + penalty;
                    };

                    const calculateScore = (trip) => {
                      const effectiveDuration = calculateEffectiveDuration(trip);
                      const price = trip.price?.amount || 350;
                      const priceWeight = 0.1;
                      const weightedPrice = price * priceWeight;
                      
                      const idealDepartureHour = 11;
                      const idealArrivalHour = 16;
                      
                      const actualDeparture = new Date(trip.departure).getHours() + new Date(trip.departure).getMinutes() / 60;
                      const actualArrival = new Date(trip.arrival).getHours() + new Date(trip.arrival).getMinutes() / 60;
                      
                      const departureDiff = Math.abs(actualDeparture - idealDepartureHour);
                      const arrivalDiff = Math.abs(actualArrival - idealArrivalHour);
                      
                      const timeWeight = 0.05;
                      const weightedTime = (departureDiff + arrivalDiff) * timeWeight;
                      
                      const complexityWeight = 0.5;  // Increased from 0.15 to 0.5
                      const complexity = trip.complexity?.value || 1;
                      const complexityPenalty = (complexity - 1) * complexityWeight * effectiveDuration;
                      
                      return effectiveDuration + weightedPrice + weightedTime + complexityPenalty;
                    };

                    return calculateScore(a) - calculateScore(b);

                  } else if (sortOption === 'fastest') {
                    // Sort by fastest journey (pure duration)
                    return getDuration(a) - getDuration(b);

                  } else if (sortOption === 'cheapest') {
                    // Sort by cheapest journey
                    const getPriceValue = (price) => {
                      const amount = price?.amount;
                      return amount < 0 || amount === undefined ? Infinity : amount;
                    };

                    return getPriceValue(a.price) - getPriceValue(b.price);
                  } else {
                    // Sort chronologically by departure time
                    return new Date(a.departure) - new Date(b.departure);
                  }
                })
                .map((trip, index) => {
                  // Lookup additional details from availableTrips using the trip ID
                  const compositeKey = createCompositeKey(trip.departure, trip.arrival);
                  const tripDetails = availableTrips[compositeKey] || {};
                  const { vendors, bookingUrls } = tripDetails;
                  
                  //const totalJourneyTime = calculateDurationPretty(trip.departure, trip.arrival);
                  //const numTransfers = trip.legs.length - 1;
                  //const longestTransfer = Math.max(...trip.legs.slice(1).map((leg, legIndex) => {
                  //  // legIndex is already offset by slice, so use trip.legs[legIndex]
                  //  const previousLeg = trip.legs[legIndex];
                  //  return new Date(leg.departure) - new Date(previousLeg.arrival);
                  //}));

                  const SHORT_DURATION_THRESHOLD = calculateDuration(trip.departure, trip.arrival) * 0.06 * (1 / (journeyWidthInPixels / 800));
                  const totalJourneyHeight = 400; // Total height in pixels for the journey container when in vertical layout
                  const MIN_LINE_HEIGHT = 20; // Minimum height in pixels
                  
                  let tripHasHiddenStationNames = false;
                  let previousShouldAddMarginStationName = false; // Track the previous leg's margin condition

                  const containsSignificantSleeperTrain = trip.legs.some(leg => leg.train_number?.startsWith('NJ ') && calculateDuration(leg.departure, leg.arrival) > 4 * 60 * 60 * 1000) || // at least 4 hours on the nightjet
                    trip.legs.some(leg => leg.train_number?.startsWith('ES ') && (leg.operator).split(',', 1)[0] === "European Sleeper" && calculateDuration(leg.departure, leg.arrival) > 4 * 60 * 60 * 1000) || // at least 4 hours on the ES train 
                    trip.legs.some(leg => (leg.operator).split(',', 1)[0] === "Urlaubs-Express");

                  const isBestOption = sortOption === 'best' && index === 0;

                  return (
                    <div className="trip-option" key={index}>
                      {/* Only show best option label when loading is complete */}
                      {isBestOption && loadingState === 'hidden' && (
                        <div className="best-option-label">Beste optie</div>
                      )}
                      {/* Grid Container for Existing Content */}
                      <div className="trip-option-content">
                        <div className="trip-time">
                          <span className="trip-time-city">{shorterStationName(trip.legs[0].origin)}</span>
                          {formatTime(trip.departure)}
                        </div>
                        <div className="trip-journey" ref={journeyRef}>
                          {trip.legs.map((leg, legIndex) => {
                            const tripDuration = calculateDuration(trip.departure, trip.arrival);
                            const legDuration = calculateDuration(leg.departure, leg.arrival);
                            const legDurationRatio = legDuration / tripDuration;
                            let legWidth = getWidth(legDuration, tripDuration); // Horizontal layout. 
                            const lineHeight = legIndex < trip.legs.length
                              ? Math.max(legDurationRatio * totalJourneyHeight, MIN_LINE_HEIGHT)
                              : 0; // Vertical layout. No line after the last leg
                            const isWalking = (leg.operator).split(',', 1)[0] === "N/A";
                            const transferDuration = legIndex > 0 ? calculateTransferTime(trip.legs[legIndex - 1].arrival, leg.departure) : 0;
                            const transferWidthInPixels = (transferDuration / tripDuration) * journeyWidthInPixels;
                            const transferWidthPercentage = (transferDuration / tripDuration) * 100;
                            const transferDurationRatio = transferDuration / tripDuration;
                            const transferHeight = transferDurationRatio * totalJourneyHeight;
                            const transfer = calculateTransferTimePretty(trip.legs[legIndex - 1]?.arrival, leg.departure);
                            const circleSize = 10;
                            const transferDetailsTop = circleSize + (lineHeight / 2);

                            // Determine transfer shape and width
                            const minTransferWidth = 30; // Add some margin
                            const minTransferHeight = 30;
                            const transferClass = !isMobile
                              ? (transferWidthInPixels > minTransferWidth ? 'rounded-rectangle' : 'circle-shape')
                              : (transferHeight > minTransferHeight ? 'rounded-rectangle-vertical' : 'circle-shape');

                            // Determine style for the circle
                            const circleStyle = {
                              width: `${circleSize}px`,  // Default width
                              height: `${circleSize}px`, // Default height
                            };
                            
                            // Adjust width and height based on transferClass
                            if (transferClass === 'rounded-rectangle') {
                              circleStyle.width = `${transferWidthPercentage}%`;
                            } else if (transferClass === 'rounded-rectangle-vertical') {
                              circleStyle.height = `${transferHeight}px`;
                            }

                            // Move leg label to the right when it is a long transfer
                            const moveLabelRight = {
                              marginLeft: transferClass === 'rounded-rectangle' ? `${transferWidthInPixels / 4 - 10}px` : '0',
                            };

                            // Update legWidth such that it includes the width of the transfer
                            legWidth += transferWidthPercentage;

                            // Calculate durations
                            const previousLegDuration = legIndex > 0 ? calculateDuration(trip.legs[legIndex - 1].departure, trip.legs[legIndex - 1].arrival) : Infinity;
                            const currentLegDuration = calculateDuration(leg.departure, leg.arrival);
                            const nextLegDuration = legIndex < trip.legs.length - 1 ? calculateDuration(trip.legs[legIndex + 1].departure, trip.legs[legIndex + 1].arrival) : Infinity;

                            // Determine if the margin should be added
                            const shouldAddMarginStationName = 
                              legIndex !== 0 && 
                              !previousShouldAddMarginStationName && (
                              previousLegDuration < SHORT_DURATION_THRESHOLD || 
                              currentLegDuration < (SHORT_DURATION_THRESHOLD * (leg.origin.length - 12)) || 
                              nextLegDuration < (SHORT_DURATION_THRESHOLD * (leg.origin.length - 12))
                            );
                            
                            // Determine if the station name should be hidden with tooltip on hover
                            const shouldHideStationName = shouldAddMarginStationName && previousShouldAddMarginStationName && nextLegDuration < SHORT_DURATION_THRESHOLD;

                            // Store the current leg's margin status for the next iteration
                            previousShouldAddMarginStationName = shouldAddMarginStationName;

                            // Prepare class name for station name
                            const stationNameClass = `station-name ${shouldHideStationName & !isMobile ? 'hidden' : ''} ${shouldAddMarginStationName & !isMobile ? 'extra-margin' : ''}`;

                            // Update the tripHasHiddenStationNames flag
                            if (shouldHideStationName) {
                              tripHasHiddenStationNames = true;
                            }

                            // Map the product types to something user-understandable
                            const trainTypeMap = {
                              regional: "Stoptrein",
                              national: "Intercity",
                              regionalExpress: "Sneltrein",
                              nationalExpress: "Hogesnelheidstrein",
                              ferry: "Veerboot",
                              suburban: "Stedelijk vervoer",
                              "N/A": "Ander vervoer",
                              //"N/A": null, // Hide "N/A"
                            };
                            
                            // Component to display the translated train type
                            const TrainTypeDisplay = ({ product }) => {
                              const trainType = trainTypeMap[product];
                            
                              if (!trainType) {
                                console.log(product); // TODO: remove after some time
                                return null; // Hide if no translation is available
                              }

                              if (leg.train_number?.startsWith('NJ ') || (leg.operator).split(',', 1)[0] === "European Sleeper" || (leg.operator).split(',', 1)[0] === "Urlaubs-Express") {
                                return <span>Nachttrein</span>;
                              }
                            
                              return <span>{trainType}</span>;
                            };                      

                            return (
                              <div className="journey-leg" key={legIndex} style={{ width: isMobile ? '100%' : `${legWidth}%` }}>
                                <div className={`circle ${transferClass}`} style={{
                                  ...circleStyle,
                                  '--transfer-height': `${transferHeight}px`
                                }}>
                                  {/* Station name */}
                                  <div className={stationNameClass} title={shorterStationName(leg.origin)}>
                                    {shorterStationName(leg.origin)}
                                  </div>
                                  {/* Transfer details */}
                                  {!isWalking && legIndex > 0 && legIndex < trip.legs.length && transfer.formattedTime !== "0m" && (
                                    <div className={`transfer-details ${transfer.colorClass}`}>
                                      <div className="transfer-icon"></div>
                                      <div className="transfer-time">
                                        {transfer.formattedTime}
                                      </div>
                                    </div>
                                  )}
                                </div>
                                {/* Line between stations */}
                                {legIndex < trip.legs.length && (
                                  <div
                                    className={isWalking ? "solid-line" : "dashed-line"}
                                    style={{ 
                                      height: isMobile ? `${lineHeight}px` : '1px',
                                      '--line-height': `${lineHeight}px`
                                    }}
                                  ></div>
                                )}
                                {/* Leg details */}
                                <div
                                  className={`leg-details ${isWalking ? 'is-walking' : ''}`}
                                  style={
                                    isMobile
                                      ? transferDetailsTop > 25
                                        ? {
                                            opacity: 1,
                                            position: 'absolute',
                                            top: `${(legDurationRatio + 0.25) * 100 + 0.75 * (transferHeight - 25) + 15}px`,
                                            left: '35px', // Adjust as needed
                                            transform: 'translateY(-50%)', // Centers the div vertically
                                          }
                                        : {
                                            opacity: 1,
                                            position: 'absolute',
                                            top: `${transferDetailsTop + 15 + 0.75 * (transferHeight - 10)}px`,
                                            left: '42px', // Adjust as needed
                                            transform: 'translateY(-50%)', // Centers the div vertically
                                            padding: '0',
                                            border: '0',
                                            boxShadow: 'none',
                                            fontSize: '0.8rem',
                                            background: 'none',
                                          }
                                      : moveLabelRight
                                  }
                                >
                                  {isWalking ? (
                                    <div className="walking-icon">🚶</div>
                                  ) : (
                                    <>
                                      {leg.operator.includes("Nederlandse Spoorwegen") ? (
                                        <img src={`${baseUrl}/icons/nederlandsespoorwegen.png`} alt="Nederlandse Spoorwegen" className="operator-icon" />
                                      ) : (leg.operator).split(',', 1)[0].startsWith("Bus") ? (
                                        <div className="bus-icon">🚌</div>
                                      ) : (
                                        <img src={`${baseUrl}/icons/${(leg.operator).split(',', 1)[0].replace(/\s+/g, '').toLowerCase()}.png`} alt={(leg.operator).split(',', 1)[0]} className="operator-icon" />
                                      )}
                                    </>
                                  )}
                                  <div className="leg-duration">{calculateDurationPretty(leg.departure, leg.arrival)}</div>
                                  <div className="leg-product"><TrainTypeDisplay product={leg.product} /></div>
                                </div>
                              </div>
                            );
                          })}
                          <div className="circle">
                            <div className="station-name">{shorterStationName(trip.legs[trip.legs.length - 1].destination)}</div>
                          </div>
                        </div>
                        <div className="trip-time">
                          {new Date(trip.arrival).getDate() !== new Date(trip.departure).getDate() && <span className="next-day-icon">☾</span>}
                          <span className="trip-time-city">{shorterStationName(trip.legs[trip.legs.length - 1].destination)}</span>
                          {formatTime(trip.arrival)}
                          {new Date(trip.arrival).getDate() !== new Date(trip.departure).getDate() && <span className="next-day">Volgende dag</span>}
                        </div>

                        <div className="vertical-dash"></div>
                        <div className="trip-price">
                          {!loadingAvailability[compositeKey] ? (
                            <>
                              {trip.price && trip.price.amount > 0 && availableTrips[compositeKey] ? (
                                <>
                                {containsSignificantSleeperTrain & trip.legs.some(leg => "European Sleeper".includes((leg.operator).split(',', 1)[0])) ? (
                                  <>
                                  {trip.legs.length === 1 ? (
                                    <table className="trip-price-table-nighttrain">
                                      <tbody>
                                        <tr>
                                          <td>
                                            {
                                              !isNaN(trip.price["nighttrain-prices"]["seat-price"]) 
                                                ? (
                                                  <span className="trip-price-amount nighttrain">
                                                    €{Math.floor(trip.price["nighttrain-prices"]["seat-price"]).toLocaleString()}
                                                  </span>
                                                )
                                                : (
                                                  <span className="trip-price-amount nighttrain soldout">
                                                    ×
                                                  </span>
                                                )
                                            }
                                          </td>
                                          <td>
                                            {
                                              !isNaN(trip.price["nighttrain-prices"]["couchette-5-price"]) 
                                                ? (
                                                  <span className="trip-price-amount nighttrain">
                                                    €{Math.floor(trip.price["nighttrain-prices"]["couchette-5-price"]).toLocaleString()}
                                                  </span>
                                                )
                                                : (
                                                  <span className="trip-price-amount nighttrain soldout">
                                                    ×
                                                  </span>
                                                )
                                            }
                                          </td>
                                          <td>
                                            {
                                              !isNaN(trip.price["nighttrain-prices"]["berth-triple-price"]) 
                                                ? (
                                                  <span className="trip-price-amount nighttrain">
                                                    €{Math.floor(trip.price["nighttrain-prices"]["berth-triple-price"]).toLocaleString()}
                                                  </span>
                                                )
                                                : (
                                                  <span className="trip-price-amount nighttrain soldout">
                                                    ×
                                                  </span>
                                                )
                                            }
                                          </td>
                                        </tr>
                                        <tr>
                                          <td><img src={`${baseUrl}/icons/seat.png`} alt="Zitplaats" /></td>
                                          <td><img src={`${baseUrl}/icons/bunk.png`} alt="Couchette" /></td>
                                          <td><img src={`${baseUrl}/icons/bed.png`} alt="Slaapwagen" /></td>
                                        </tr>
                                      </tbody>
                                    </table>
                                  ) : (
                                    <span className="price-not-available">Deze route wordt niet op één ticket verkocht</span>
                                  )}
                                  </>
                                ) : (
                                  <div className="trip-price-amount">
                                    €{Math.floor(trip.price.amount).toLocaleString()}<sup>,{(trip.price.amount % 1).toFixed(2).substring(2)}</sup>
                                  </div>
                                )}
                                </>
                              ) : (
                                <>
                                {containsSignificantSleeperTrain ? (
                                  <>
                                  {vendors && vendors.length > 0 ? (
                                    <span className="price-differs">Prijs verschilt per accommodatietype. Klik op Boeken voor de prijs.</span>
                                  ) : (
                                    <span className="price-not-available">Deze route wordt niet op één ticket verkocht</span>
                                  )}
                                  </>
                                ) : (
                                  <>
                                  {trip.legs.some(leg => noPriceAPIOperators.includes((leg.operator).split(',', 1)[0])) ? (
                                    <>
                                    {vendors && vendors.length > 0 ? (
                                      <span className="price-not-available">Klik op Boeken voor de prijs</span>
                                    ) : (
                                      <span className="price-not-available">Er lijkt iets mis te gaan met deze route, of niemand verkoopt dit ticket.</span>
                                    )}
                                    </>
                                  ) : (
                                    <span className="price-not-available">⌛ Prijs nog niet beschikbaar</span>
                                  )}
                                  </>
                                )}
                                </>
                              )}

                              <div className="price-terms">
                                {(() => {
                                  const totalPassengers = passengerCounts.adults + 
                                    passengerCounts.babies + 
                                    passengerCounts.children4to5 + 
                                    passengerCounts.children6to11 + 
                                    passengerCounts.children12to14 + 
                                    passengerCounts.youth15 + 
                                    passengerCounts.youth16to25 + 
                                    passengerCounts.seniors;
                                  
                                  const isSingleAdult = passengerCounts.adults === 1 && totalPassengers === 1;
                                  
                                  return !(totalPassengers === 0 || isSingleAdult) && (
                                    <span className="price-multi-passenger">prijs per volwassene,&nbsp;</span>
                                  );
                                })()}
                                <span className="price-single">
                                  {returnDate && (
                                    <>
                                      {!isViewingReturn && !savedOutboundTrip && (
                                        <button 
                                          className="save-trip-button"
                                          onClick={() => {
                                            // Save current trip as outbound trip before switching
                                            saveOutboundTrip(trip);
                                            // Only swap locations and fetch new trips if we don't have both trips saved
                                            if (!savedReturnTrip) {
                                              handleSwapLocations(false); // Don't clear saved trips
                                              setIsViewingReturn(!isViewingReturn);
                                              setTravelDate(isViewingReturn ? returnDate : departureDate);
                                              resetState();
                                            }
                                          }}
                                        >
                                          <span className="save-icon">☐</span>
                                          <span className="save-text">Heenreis opslaan</span>
                                        </button>
                                      )}
                                      {isViewingReturn && !savedReturnTrip && (
                                        <button 
                                          className="save-trip-button"
                                          onClick={() => {
                                            // Save current trip as return trip before switching
                                            saveReturnTrip(trip);
                                            // Only swap locations and fetch new trips if we don't have both trips saved
                                            if (!savedOutboundTrip) {
                                              handleSwapLocations(false); // Don't clear saved trips
                                              setIsViewingReturn(!isViewingReturn);
                                              setTravelDate(isViewingReturn ? returnDate : departureDate);
                                              resetState();
                                            }
                                          }}
                                        >
                                          <span className="save-icon">☐</span>
                                          <span className="save-text">Terugreis opslaan</span>
                                        </button>
                                      )}
                                      {savedOutboundTrip && savedReturnTrip && (
                                        <button 
                                          className="clear-saved-trips-button"
                                          onClick={() => {
                                            showSavedTripsOnly();
                                          }}
                                        >
                                          Wissen en begin opnieuw
                                        </button>
                                      )}
                                    </>
                                  )}
                                </span>
                              </div>

                              {vendors && vendors.length > 0 && (
                                <div className="trip-tickets">
                                  <div className="ticket-label">
                                    <span className="tickets">TICKETS</span>
                                  </div>
                                  <div className="vendor-list">
                                    {vendors
                                      .filter(vendor => filters.vendors[vendor]) // Only show enabled vendors
                                      .map((vendor, idx) => {
                                        let vendorLogo = "";
                                        if (vendor === "NS") {
                                          vendorLogo = "nederlandsespoorwegen";
                                        } else if (vendor === "DB") {
                                          vendorLogo = "dbfernverkehrag";
                                        } else if (vendor === "European Sleeper") {
                                          vendorLogo = "europeansleeper";
                                        }

                                        return (
                                          <React.Fragment key={`fragment-${compositeKey}-${idx}`}>
                                            <div
                                              key={`offer-${compositeKey}-${idx}-${vendor}`}
                                              className={`vendor-box ${idx <= 1 ? 'auto-expanded' : visibleVendorBoxes[compositeKey] ? 'expanded' : ''}`}
                                              ref={el => vendorBoxRefs.current[compositeKey] = el}
                                            >
                                              {/* eslint-disable-next-line react/jsx-no-target-blank */}
                                              <a href={bookingUrls[vendor]} target="_blank" rel="sponsored nofollow">
                                                <div className="vendor-logo-container">
                                                  <img src={`${baseUrl}/icons/${vendorLogo}.png`} alt="Vendor Logo" className="vendor-logo" />
                                                </div>
                                                <div className="book-text-container">
                                                  <span className="book-text">Boeken</span>
                                                </div>
                                              </a>
                                              {vendor === 'DB' && trip.via && (
                                                <div className="experimental-notice">
                                                  Experimentele functie - Controleer de route op de DB-website!
                                                </div>
                                              )}
                                            </div>
                                          </React.Fragment>
                                        );
                                      })}
                                  </div>
                                </div>
                              )}
                            </>
                          ) : (
                            <div>Loading...</div>
                          )}
                        </div>
                        <div className="trip-summary">
                          <div className="summary-item">
                            <span className="desktop-only">Reisduur: </span>
                            <span className="mobile-only">⏱️ </span>
                            <strong>{formatDuration(trip)}</strong>
                          </div>
                          <div className="summary-item">
                            <span className="desktop-only">Overstappen: </span>
                            <span className="mobile-only">🔄 </span>
                            <strong>{trip.legs.length - 1}</strong>
                          </div>
                          <div className="summary-item complexity-indicator">
                            {(trip.complexity?.value || 1) > 1 ? (
                              <div 
                                className="complexity-clickable"
                                title={trip.complexity?.tooltip}  // Keep existing desktop tooltip
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setMobileTooltipText(trip.complexity?.tooltip);
                                  setMobileTooltipVisible(true);
                                }}
                              >
                                <span className={`complexity-dot complexity-dot-${trip.complexity?.value || 1}`}></span>
                                <span className="complexity-label">{getComplexityLabel(trip.complexity?.value || 1)}</span>
                                <span className="complexity-tooltip">ⓘ</span>
                              </div>
                            ) : (
                              <>
                                <span className={`complexity-dot complexity-dot-${trip.complexity?.value || 1}`}></span>
                                <span className="complexity-label">{getComplexityLabel(trip.complexity?.value || 1)}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="trip-remarks">
                          {(() => {
                            const hasEuropeanSleeperLeg = trip.legs.some(leg => 
                              (leg.operator).split(',', 1)[0] === "European Sleeper"
                            );
                            const isMultiLegJourney = trip.legs.length > 1;
                            const shouldShowWarning = hasEuropeanSleeperLeg && isMultiLegJourney;

                            // Find the European Sleeper leg
                            const europeanSleeperLeg = trip.legs.find(leg => 
                              (leg.operator).split(',', 1)[0] === "European Sleeper"
                            );

                            // Determine the station name based on the viewing direction
                            const europeanSleeperStation = isViewingReturn 
                              ? europeanSleeperLeg ? europeanSleeperLeg.destination : "het European Sleeper-station"
                              : europeanSleeperLeg ? europeanSleeperLeg.origin : "het European Sleeper-station";

                            return shouldShowWarning && (
                              <div className="remark">
                                &#9432; European Sleeper verkoopt alleen rechtstreekse tickets. Overweeg om een los kaartje {isViewingReturn ? 'vanaf' : 'tot aan'} {europeanSleeperStation} te kopen.
                                {!isViewingReturn && " Zorg wel dat je daar op tijd bent, aangezien de overstap dan voor eigen risico komt."}
                              </div>
                            );
                          })()}
                          {containsSignificantSleeperTrain && (
                            <div className="remark">&#9432; Deze reis bevat een slaaptrein. Lees onze <a href="/hoe-werkt-een-nachttrein/" target="_blank">informatie over nachttreinen</a>.</div>
                          )}
                          {tripHasHiddenStationNames && (
                            <div className="remark">&#9432; We hebben enkele stationsnamen moeten verbergen in het overzicht vanwege de beschikbare ruimte.</div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
        <div className={`filter-panel-wrapper${isFilterPanelOpen ? ' open' : ''}`}>
          {/* Desktop sidebar filter panel - only visible on large screens */}
          <div className="filter-sidebar desktop-only">
            <div className="filter-panel">
              <FilterPanel />
            </div>
          </div>
        </div>
      </div>

      {/* Add this at the bottom, just before the final closing div */}
      <div className="trip-disclaimer">
        Getoonde resultaten enkel ter inspiratie. Controleer altijd voor het boeken alle gegevens, zoals datum, tijden, route, reisgezelschap en ticketvoorwaarden.
      </div>

      {/* Add this at the bottom of your component, before the closing tag */}
      {mobileTooltipVisible && (
        <div className="mobile-tooltip">
          {mobileTooltipText}
        </div>
      )}
    </div>
  );
};

const formatTime = (time) => {
  const date = new Date(time);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Helper function to safely format the date in a well-readable shorthand notation
function getFormattedDateShorthand(departureDate) {
  if (!departureDate || typeof departureDate === 'object') {
    return 'No date available';
  }

  try {
    const parsedDate = new Date(convertToISOFormat(departureDate));
    
    // Check if the parsedDate is a valid Date
    if (isNaN(parsedDate.getTime())) {
      throw new Error('Invalid date');
    }

    const currentYear = new Date().getFullYear();
    const departureYear = parsedDate.getFullYear();

    if (departureYear === currentYear) {
      return format(parsedDate, 'd') + ' ' + monthNameInDutch(parsedDate);
    } else {
      return format(parsedDate, 'dd-MM-yyyy');
    }
  } catch (error) {
    console.error('Date formatting error:', error);
    return 'Invalid date';
  }
}

function convertToISOFormat(dateStr) {
  // Check if dateStr is a string
  if (typeof dateStr !== 'string') {
    console.error(`Input must be a string: ${dateStr}`);
    return;
  }

  // Split the date string into components
  const parts = dateStr.split('-');

  // Validate the format (DD-MM-YYYY)
  if (parts.length !== 3) {
    console.error('Invalid date format. Expected format is DD-MM-YYYY');
    return;
  }

  const [day, month, year] = parts;

  // Validate that day, month, and year are numbers and within valid ranges
  if (
    isNaN(day) || isNaN(month) || isNaN(year) || 
    month < 1 || month > 12 || 
    day < 1 || day > 31 || 
    year.length !== 4
  ) {
    console.error('Invalid date components. Ensure DD, MM, and YYYY are valid numbers');
    return;
  }

  // Pad month and day with leading zeros if necessary
  const paddedMonth = month.padStart(2, '0');
  const paddedDay = day.padStart(2, '0');

  // Return the date in ISO format (YYYY-MM-DD)
  return `${year}-${paddedMonth}-${paddedDay}`;
}

const calculateDurationPretty = (start, end) => {
  let startTime, endTime;

  // Check if start is a number (milliseconds since the epoch or duration)
  if (typeof start === 'number') {
    if (start > 1000000000000) { // Likely a timestamp in milliseconds
      startTime = new Date(start);
    } else {
      // Handle this as a time difference, assuming end is also a number
      startTime = 0;
      endTime = start;
    }
  } else {
    // Otherwise, assume start is a date string
    startTime = new Date(start);
  }

  // Check if end is a number (milliseconds since the epoch or duration)
  if (typeof end === 'number') {
    if (end > 1000000000000) { // Likely a timestamp in milliseconds
      endTime = new Date(end);
    } else if (typeof startTime === 'number') {
      // Handle this as a time difference if startTime was set to 0
      endTime = end;
    } else {
      // Handle mismatch (e.g., start as a date and end as a duration in ms)
      console.error('Mismatched types for start and end. Cannot calculate duration.');
      return 'Invalid inputs';
    }
  } else {
    // Otherwise, assume end is a date string
    endTime = new Date(end);
  }

  // Check if parsing resulted in valid dates
  if (isNaN(startTime) || isNaN(endTime)) {
    console.error('Invalid start or end time');
    return 'Invalid time';
  }

  // Calculate duration
  const duration = (endTime - startTime) / (1000 * 60); // duration in minutes
  const hours = Math.floor(duration / 60);
  const minutes = Math.round(duration % 60);
  
  return `${hours}:${minutes < 10 ? '0' : ''}${minutes}`;
};

const calculateDuration = (start, end) => {
  const startTime = new Date(start);
  const endTime = new Date(end);
  return endTime - startTime;
};  

const getWidth = (duration, totalDuration) => {
  if (totalDuration <= 0) {
    console.error("Invalid total duration:", totalDuration);
    return 0;
  }
  return (duration / totalDuration) * 100;
};

const calculateTransferTime = (arrival, departure) => {
  return new Date(departure) - new Date(arrival);
};

const calculateTransferTimePretty = (arrival, departure) => {
  if (!arrival || !departure) { return }
  const arrivalTime = new Date(arrival);
  const departureTime = new Date(departure);
  const transferDuration = (departureTime - arrivalTime) / (1000 * 60); // duration in minutes
  const hours = Math.floor(transferDuration / 60);
  const minutes = transferDuration % 60;
  const formattedTime = `${hours ? hours + 'h ' : ''}${minutes}m`;

  let colorClass = '';
  if (transferDuration < 10) {
    colorClass = 'short-transfer';
  } else if (transferDuration > 45) {
    colorClass = 'long-transfer';
  }

  return { formattedTime, colorClass };
};

const shorterStationName = (name) => {
  return name.length > 20 ? name.substring(0, 20) + '...' : name;
};

const editDistance = (s1, s2) => {
  s1 = s1.toLowerCase();
  s2 = s2.toLowerCase();

  const costs = [];
  for (let i = 0; i <= s1.length; i++) {
    let lastValue = i;
    for (let j = 0; j <= s2.length; j++) {
      if (i === 0) {
        costs[j] = j;
      } else {
        if (j > 0) {
          let newValue = costs[j - 1];
          if (s1.charAt(i - 1) !== s2.charAt(j - 1)) {
            newValue = Math.min(Math.min(newValue, lastValue), costs[j]) + 1;
          }
          costs[j - 1] = lastValue;
          lastValue = newValue;
        }
      }
    }
    if (i > 0) {
      costs[s2.length] = lastValue;
    }
  }
  return costs[s2.length];
};

const hardcodedMappings = {
  "Koebenhavn H": "DKKHX",
  "Innsbruck Hbf": "ATALA",
  // Add other hardcoded mappings here as needed
};

// Function to get NS encoding from station name
const getNSEncoding = (stationName) => {
  // Standardize the input by trimming and converting to lowercase
  const standardizedInput = stationName.trim().toLowerCase();

  // Check the hardcoded mappings first
  if (hardcodedMappings[stationName]) {
    return hardcodedMappings[stationName];
  }

  // Helper function to normalize station names for comparison
  const normalizeStationName = (name) => {
    let result = name
      .toLowerCase()
      .replace(/[.\s-]+/g, ' ') // Replace dots, spaces, and hyphens with a single space
      .replace(/\s*international\s*/g, '') // Remove "international"
      .replace(/\s*centraal\s*/g, '') // Remove "centraal"
      .replace(/\s*central\s*/g, '') // Remove "central"
      .replace(/\s*station\s*/g, '') // Remove "station"
      .replace(/\s*hauptbahnhof\s*/g, '') // Remove "hauptbahnhof"
      .replace(/\s*hbf\s*/g, '') // Remove "hbf"
      .replace(/\s*bahnhof\s*/g, '') // Remove "bahnhof"
      .replace(/\s*hl\.n\.\s*/g, '') // Remove "hl.n."
      .replace(/\s*hl\s*n\s*/g, '') // Remove "hl n" (after dots are converted to spaces)
      .replace(/\s+hln\s*$/g, '') // Remove "hln" at the end of the name
      .trim();
    
    return result;
  };

  // Duplicate similarity function here for local use
  const similarity = (s1, s2) => {
    let longer = s1;
    let shorter = s2;
    if (s1.length < s2.length) {
      longer = s2;
      shorter = s1;
    }
    const longerLength = longer.length;
    if (longerLength === 0) {
      return 1.0;
    }
    return (longerLength - editDistance(longer, shorter)) / parseFloat(longerLength);
  };

  // Normalize the input station name
  const normalizedInput = normalizeStationName(standardizedInput);

  // First try exact match with normalized names
  let station = stationMappings.find(st => 
    normalizeStationName(st.name) === normalizedInput
  );

  // If no exact match, try fuzzy matching with normalized names
  if (!station) {
    station = stationMappings.find(st => {
      const normalizedMapping = normalizeStationName(st.name);
      return similarity(normalizedMapping, normalizedInput) > 0.8;
    });
  }

  // If still no match, try with diacritics removed from input
  if (!station) {
    const inputWithoutDiacritics = normalizedInput
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    const normalizedInputWithoutDiacritics = normalizeStationName(inputWithoutDiacritics);
    
    station = stationMappings.find(st => {
      const normalizedMapping = normalizeStationName(st.name);
      const normalizedMappingWithoutDiacritics = normalizedMapping
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
      
      return normalizedMappingWithoutDiacritics === normalizedInputWithoutDiacritics;
    });
  }

  // If still no match, try fuzzy matching with diacritics removed
  if (!station) {
    const inputWithoutDiacritics = normalizedInput
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');
    const normalizedInputWithoutDiacritics = normalizeStationName(inputWithoutDiacritics);
    
    station = stationMappings.find(st => {
      const normalizedMapping = normalizeStationName(st.name);
      return similarity(normalizedMapping, normalizedInputWithoutDiacritics) > 0.8;
    });
  }

  // Debugging Logs
  if (!station) {
    console.warn(`No mapping found for station "${stationName}". Returning "NS-MAPPING-ERROR".`);
  }

  // Return the mapped code if found, or fallback to "NS-MAPPING-ERROR"
  return station ? station.code : "NS-MAPPING-ERROR";
};

function slugify(text) {
  return text
    .normalize('NFD') // Normalize the string to decompose combined letters
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritical marks
    .replace(/[^a-zA-Z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .trim() // Remove leading and trailing spaces
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .toLowerCase(); // Convert to lowercase
}

const monthNameInDutch = (date) => {
  // Ensure the date is a valid Date object
  const validDate = new Date(date);
  if (isNaN(validDate)) {
    return;
  }

  // Create an Intl.DateTimeFormat object for Dutch locale
  const formatter = new Intl.DateTimeFormat('nl-NL', { month: 'long' });

  // Format the month
  const monthName = formatter.format(validDate);

  return monthName;
};

// Modify the function signature to accept setShortTransferStations
const calculateComplexity = (trip, shortTransferStationsRef) => {
  let complexity = 1;
  let reasons = [];
  let currentShortTransfers = [];

  // Check for London St. Pancras
  const hasLondon = trip.legs.some(leg => 
    leg.origin === "London St. Pancras International" || 
    leg.destination === "London St. Pancras International"
  );
  
  if (hasLondon && complexity < 3) {
    complexity++;
    reasons.push("Reis in/uit Schengengebied");
  }

  // Check for night departure/arrival
  const departureHour = new Date(trip.departure).getHours();
  const arrivalHour = new Date(trip.arrival).getHours();
  const isNightTime = (hour) => hour >= 0 && hour < 6;
  
  if ((isNightTime(departureHour) || isNightTime(arrivalHour)) && complexity < 3) {
    complexity++;
    reasons.push(isNightTime(departureHour) && isNightTime(arrivalHour) 
      ? "Vertrek en aankomst in de nacht"
      : isNightTime(departureHour) 
        ? "Vertrek in de nacht" 
        : "Aankomst in de nacht"
    );
  }

  // Define Dutch operators
  const dutchOperators = ['Nederlandse Spoorwegen', 'Arriva Nederland', 'RRReis', 'Blauwnet'];
  const localTrainTypes = ['regional', 'regionalExpress', 'suburban'];
  
  let foreignTransfers = 0;
  let hasLongTransfer = false;
  
  for (let i = 1; i < trip.legs.length; i++) {
    const fromOperator = trip.legs[i-1].operator.split(',')[0];
    
    // Check transfer time
    const transferTime = (new Date(trip.legs[i].departure) - new Date(trip.legs[i-1].arrival)) / (1000 * 60);
    if (transferTime > 180 && !hasLongTransfer && complexity < 3) {  // 3 hours = 180 minutes
      complexity = 3;  // Jump straight to 3
      hasLongTransfer = true;
      reasons.push("Overstap langer dan 3 uur");
    } else if (transferTime > 90 && !hasLongTransfer && complexity < 3) {
      complexity++;
      hasLongTransfer = true;
      reasons.push("Overstap langer dan 1,5 uur");
    }
    
    // Check for complex international transfers
    if ((!dutchOperators.includes(fromOperator) || 
         trip.legs[i-1].train_number?.includes('ICE') ||
         /^IC \d{3}$/.test(trip.legs[i-1].train_number || '')) && 
        !localTrainTypes.includes(trip.legs[i].product) &&
        transferTime < 35 &&
        transferTime >= 1) {
      
      // If there is a previous leg and it was a Transfer, look at the leg before that
      if (i >= 2 && trip.legs[i-1].train_number === "Transfer") {
        // Only count as complex if leg before Transfer was non-Dutch
        const operatorBeforeTransfer = trip.legs[i-2].operator.split(',')[0];
        if (!dutchOperators.includes(operatorBeforeTransfer)) {
          foreignTransfers++;
          // Add station tracking here
          currentShortTransfers.push(trip.legs[i-2].destination);
        }
      } else {
        foreignTransfers++;
        // Add station tracking here
        currentShortTransfers.push(trip.legs[i-1].destination);
      }
    }
  }

  // Add this block after the loop
  if (currentShortTransfers.length > 0) {
    currentShortTransfers.forEach(station => {
      shortTransferStationsRef.current.set(
        station, 
        (shortTransferStationsRef.current.get(station) || 0) + 1
      );
    });
  }
  
  if (foreignTransfers > 0) {
    complexity += Math.min(foreignTransfers, 2); // Add max 2 to complexity
    reasons.push(`Krappe overstap${foreignTransfers > 1 ? 'pen' : ''} buiten Nederland`);
  }

  // Check for walking sections, excluding Eurostar transfers
  const hasWalking = trip.legs.some(leg => 
    leg.product === "N/A" && 
    !(leg.origin.includes("Eurostar") || leg.destination.includes("Eurostar"))
  );
  
  if (hasWalking && complexity < 3) {
    complexity++;
    reasons.push("Gedeelte met ander vervoer");
  }

  // Count total number of transfers
  const totalTransfers = trip.legs.length - 1;
  
  // Add 0.2 for each transfer that wasn't already counted
  if (totalTransfers > 0) {
    const transferComplexity = 0.2 * totalTransfers;
    complexity += transferComplexity;
    
    if (transferComplexity >= 2) {
      reasons.push("Erg veel overstappen");
    } else if (transferComplexity >= 1) {
      reasons.push("Veel overstappen");
    }
  }

  // Format reasons with proper Dutch conjunction
  const formatReasons = (reasons) => {
    if (reasons.length === 0) return '';
    if (reasons.length === 1) return reasons[0];
    if (reasons.length === 2) return `${reasons[0]} en ${reasons[1]}`;
    return reasons.slice(0, -1).join(', ') + ' en ' + reasons[reasons.length - 1];
  };

  // Cap complexity at 3 and round down to integer
  complexity = Math.floor(Math.min(complexity, 3));
  
  return {
    value: complexity,
    tooltip: formatReasons(reasons)
  };
};

export default TripOptions;
